import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  AdjustmentsHorizontalIcon,
  ArrowsRightLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  ExclamationCircleIcon,
  EyeSlashIcon,
  FunnelIcon,
  ListBulletIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  ViewColumnsIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ObjectID } from "bson";
import clsx from "clsx";
import dayjs from "dayjs";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  FixedSizeList as List,
  ListChildComponentProps,
  VariableSizeList,
} from "react-window";
import {
  ParentStatus,
  StatusData,
  StatusGroupData,
} from "src/api/General/status-groups";
import {
  JourneyData,
  useAddStage,
  useGetJourney,
  useGetJourneyStages,
  useUpdateJourney,
  useUpdateJourneyStage,
} from "src/api/Services/Journeys/journeys";
import { PhaseData, useGetPhase } from "src/api/Services/SharedSpace/phases";
import {
  TaskData,
  useGetJourneyStageTasks,
  useGetPhaseTasks,
  useUpdatePhaseTasks,
} from "src/api/Services/Tasks/tasks";
import { JourneyStageObj } from "src/components/Services/Journeys/Journey/types";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
// import Tasker from "src/components/Services/Reusable/Tasks/Elements/Tasker";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  CalendarIcon,
  CaretSortIcon,
  CheckIcon,
  CircleIcon,
  DragHandleDots2Icon,
  Pencil2Icon,
  TriangleRightIcon,
} from "@radix-ui/react-icons";
import { useHotkeys } from "react-hotkeys-hook";
import {
  InstanceWorkingTimeSettings,
  useGetInstanceWorkingTimeSettings,
} from "src/api/General/settings";
import { useAssignUserToTasksWithRoleInProject } from "src/api/Misc/Settings/roles/roles";
import { PriorityData } from "src/api/Services/Journeys/journeyTemplates";
import { useGetProjectMembers } from "src/api/Services/Projects/projects";
import { useDeleteJourneyStage } from "src/api/Services/Tasks/journeys";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Button } from "src/components/ui/button";
import { Calendar } from "src/components/ui/calendar";
import { Checkbox } from "src/components/ui/checkbox";
import { Count } from "src/components/ui/count";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { TextInput } from "src/components/ui/text-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useNewTaskForExternalUser } from "src/pages/Services/Tasks/NewTaskForExternalUser";
import { useNewTaskForInternalUser } from "src/pages/Services/Tasks/NewTaskForInternalUser";
import {
  StatusSelect,
  getStatusItem,
  getStatusSymbol,
} from "src/pages/Services/Tasks/Task";
import { useStageHoverActions } from "src/pages/Services/Tasks/stage-over-store";
import { useStatusGroups } from "src/pages/status-groups/status-groups-context-provider";
import { useRealmApp } from "src/store/RealmApp";
import { calculateEndDate } from "src/utils/forecast";
import { getIdentifier } from "src/utils/functions/tasks";
import { cn } from "src/utils/ui/ui";
import {
  MilestoneIcon,
  getPriorityIconAndLabel,
} from "../../../Template/TemplateEdit/Stages/TemplateStages";
import { ProjectGroup, useJourney } from "../../JourneyContextProvider";
import { TaskCard } from "../Task/TaskCard";
import NewTaskButton from "./NewTaskButton";
import {
  useGetDistribution,
  useIsTaskSelected,
  useNumberOfSelectedTasks,
  useSelectedTasks,
  useSelectionActions,
} from "./task-multi-selection-store";

export const Stages = ({ journeyId }: { journeyId: string }) => {
  const { phaseId } = useParams();
  // const { sharedSpace } = useGetSharedSpace(new ObjectID(sharedSpaceId));
  const { data: phase } = useGetPhase(String(phaseId));
  const { stages } = useGetJourneyStages(new ObjectID(journeyId));
  const journeyCtx = useJourney();
  const statusGroupsCtx = useStatusGroups();
  const statusGroup = statusGroupsCtx.state.statusGroups.find(
    (sg) => sg._id === phase?.statusGroup._id
  );
  const { journey } = useGetJourney(new ObjectID(journeyId));
  const { data: tasks } = useGetPhaseTasks(String(phaseId));
  const [newStageName, setNewStageName] = useState("");
  const addStageMutation = useAddStage();
  const searchParams = new URLSearchParams(window.location.search);
  const newTaskForInternalUserCtx = useNewTaskForInternalUser();
  const newTaskForExternalUserCtx = useNewTaskForExternalUser();
  const responsibility = searchParams.get("responsibility");
  const [filterGroup, setFilterGroup] = useState<FilterGroup>(() => {
    const savedFilterGroup = localStorage.getItem(`filter_${phaseId}`);
    return savedFilterGroup
      ? JSON.parse(savedFilterGroup)
      : {
          logic: "AND",
          children: [],
        };
  });

  const { clearSelection } = useSelectionActions();

  useEffect(() => {
    clearSelection();
  }, []);

  useHotkeys("esc", () => {
    clearSelection();
  });

  useEffect(() => {
    if (addStageMutation.isSuccess) {
      setNewStageName("");
    }
  }, [addStageMutation.isSuccess]);

  useEffect(() => {
    if (filterGroup.children.length === 0) {
      localStorage.removeItem(`filter_${phaseId}`);
    } else {
      localStorage.setItem(`filter_${phaseId}`, JSON.stringify(filterGroup));
    }
  }, [filterGroup]);

  // useEffect(() => {
  //   if (responsibility === "my") {
  //     journeyCtx.dispatch({
  //       type: "VIEW_TEAM_UPDATE",
  //       responsibility: "my",
  //     });
  //     journeyCtx.dispatch({
  //       type: "VIEW_SHOW_ACTIVE_AND_INCOMPLETE_TASKS_UPDATE",
  //       showOnlyActiveAndIncompleteTasks: true,
  //     });
  //   }
  //   if (responsibility === "internal") {
  //     journeyCtx.dispatch({
  //       type: "VIEW_TEAM_UPDATE",
  //       responsibility: "internal",
  //     });
  //     journeyCtx.dispatch({
  //       type: "VIEW_SHOW_ACTIVE_AND_INCOMPLETE_TASKS_UPDATE",
  //       showOnlyActiveAndIncompleteTasks: true,
  //     });
  //   }
  //   if (responsibility === "external") {
  //     journeyCtx.dispatch({
  //       type: "VIEW_TEAM_UPDATE",
  //       responsibility: "external",
  //     });
  //     journeyCtx.dispatch({
  //       type: "VIEW_SHOW_ACTIVE_AND_INCOMPLETE_TASKS_UPDATE",
  //       showOnlyActiveAndIncompleteTasks: true,
  //     });
  //   }
  //   // if (overdueTasks) {
  //   //   journeyCtx.dispatch({
  //   //     type: "VIEW_TEAM_UPDATE",
  //   //     responsibility: "overdue",
  //   //   });
  //   // }
  // }, []);

  const filteredTasks = useMemo(
    () =>
      filterAndBuildTree(
        tasks?.filter((t) => {
          if (journeyCtx.state.isExternal) {
            return t.isVisible;
          }
          return true;
        }) ?? [],
        (t) => {
          if (filterGroup.logic === "AND") {
            for (let i = 0; i < filterGroup.children.length; i++) {
              const child = filterGroup.children[i] as Filter;
              if (child.field === "status") {
                if (child.value.length > 0) {
                  if (!child.value.includes(t.customStatus)) {
                    return false;
                  }
                }
              }
              if (child.field === "state" && child.value !== null) {
                if (child.value === "active") {
                  if (!t.dueDate) {
                    return false;
                  }
                }
              }
              if (child.field === "company" && child.value !== null) {
                if (child.value) {
                  if (t.taskerType === "external") {
                    return false;
                  }
                } else {
                  if (t.taskerType === "internal") {
                    return false;
                  }
                }
              }
              if (child.field === "task-priority" && child.value.length > 0) {
                if (!t.priority) {
                  return false;
                }
                if (!child.value.includes(t.priority)) {
                  return false;
                }
              }
            }
            return true;
          } else {
            for (let i = 0; i < filterGroup.children.length; i++) {
              const child = filterGroup.children[i] as Filter;
              if (child.field === "status") {
                if (child.value.length > 0) {
                  if (child.value.includes(t.customStatus)) {
                    return true;
                  }
                }
              } else if (child.field === "state" && child.value !== null) {
                if (child.value === "active") {
                  if (!!t.dueDate) {
                    return true;
                  }
                }
              } else if (child.field === "company" && child.value !== null) {
                if (child.value) {
                  if (t.taskerType === "internal") {
                    return true;
                  }
                } else {
                  if (t.taskerType === "external") {
                    return true;
                  }
                }
              } else if (
                child.field === "task-priority" &&
                child.value.length > 0
              ) {
                if (t.priority && child.value.includes(t.priority)) {
                  return true;
                }
              } else if (journeyCtx.state.isExternal) {
                if (t.isVisible) {
                  return true;
                }
              }
            }
            return false;
          }
        }
      ),
    [filterGroup, journeyCtx.state.isExternal, tasks]
  );

  useEffect(() => {
    console.log("filtered tasks")
  }, [filteredTasks])

  // console.log(filteredTasks)
  // tasks?.filter((t) => {

  //   }
  // }) ?? [];

  // console.log(filteredTasks)

  return (
    <>
      {!!tasks && !!phase && !!journey && !!stages && statusGroup && (
        <div className="relative flex h-full flex-1 flex-col">
          {(tasks.length > 0 || stages.length > 1) && (
            <div
              className={clsx(
                "flex h-14 shrink-0 items-center gap-4 px-8",
                // journeyCtx.state.view.view === "list" &&
                "border-b border-gray-200/[.65]"
              )}
            >
              <div className="flex-1">
                <div className="flex items-center justify-between space-x-2">
                  {/* <div className="space-y-1">
                <h3 className="text-xs text-gray-400">View</h3>
                <ViewFilter />
              </div> */}

                  <div className="flex items-center space-x-2">
                    <Filter
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      // localStorageKey="project-filter"
                      filterModels={[
                        { field: "status", statuses: statusGroup.statuses },
                        // { field: "dueDate" },
                        { field: "state" },
                        ...(phase.collaborationType === "external"
                          ? [
                              {
                                field: "company" as "company",
                                internalCompany: phase.instance.name,
                                externalCompany: phase.customer?.name ?? "",
                              },
                            ]
                          : []),
                        { field: "task-priority" },
                      ]}
                    />
                    <Display />
                    {!journeyCtx.state.isExternal && (
                      <RoleAssignment phaseId={String(phaseId)} />
                    )}
                  </div>
                  <NewTaskButton phase={phase} journeyId={journeyId} />

                  {/* <TaskerFilter
                customerName={phase.customer?.name}
                instanceName={phase.instance?.name}
              />
              <TasksFilter /> */}
                  {/* <SubtasksFilter /> */}
                  {/* <button
                className={clsx(
                  "rounded-md border px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
                  // journeyCtx.state.view.showActiveAndIncompleteTasks
                  //   ? "border-primary-main bg-primary-100 text-primary-600"
                  //   : ""
                )}
                onClick={() => {
                  journeyCtx.dispatch({
                    type: "VIEW_SHOW_ACTIVE_AND_INCOMPLETE_TASKS_UPDATE",
                    showOnlyActiveAndIncompleteTasks:
                      !journeyCtx.state.view.showOnlyActiveAndIncompleteTasks,
                  });
                }}
              >
                {journeyCtx.state.view.showOnlyActiveAndIncompleteTasks
                  ? "View Inactive Tasks"
                  : "Hide Inactive Tasks"}
              </button> */}
                  {/* <DisplayOptions /> */}
                  {/* <CompletedFilter />
              <SubtasksFilter />
              <InactiveFilter /> */}
                  {/* <div className="space-y-1">
                <h3 className="text-xs text-gray-400">Tasker</h3>
                <TeamFilter
                  customerName={phase.customer?.name}
                  instanceName={phase.instance?.name}
                />
              </div> */}
                  {/* <div className="space-y-1">
                <h3 className="text-xs text-gray-400">Status</h3>
                <TaskStatusFilter />
              </div> */}
                  {/* <div className="h-2 w-2 rounded-full bg-gray-500"></div> */}
                  {/* <div className="space-y-1">
                <h3 className="text-xs text-gray-400">State</h3>
                <TaskStateFilter />
              </div> */}
                </div>
              </div>
            </div>
          )}
          {filteredTasks.length > 0 || stages.length > 1 ? (
            <>
              {journeyCtx.state.view.view === "kanban" ? (
                <KanbanView
                  stages={stages}
                  journeyId={journeyId}
                  statusGroup={statusGroup}
                  tasks={filteredTasks}
                  phase={phase}
                />
              ) : journeyCtx.state.view.view === "gantt" ? (
                <div className="flex w-full  flex-1 flex-col overflow-auto">
                  <GanttView
                    tasks={filteredTasks}
                    stages={stages}
                    journeyId={journeyId}
                    statusGroup={statusGroup}
                  />
                </div>
              ) : (
                <ListView
                  tasks={filteredTasks}
                  stages={stages}
                  journeyId={journeyId}
                  statusGroup={statusGroup}
                />
              )}
            </>
          ) : (
            <div className="mx-auto mt-[10vh] w-fit">
              <div className="flex">
                <div className="h-20 w-20"></div>
                <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
                <div className="h-20 w-20"></div>
              </div>
              <div className="flex">
                <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
                <div className="w-[420px] border bg-gray-50/70 p-12">
                  <div className="text-lg font-medium">Get Started</div>
                  <div className="pt-4 font-light leading-relaxed text-gray-700">
                    Begin by creating a task. If you want to group tasks, create
                    a stage first.
                  </div>
                  <div className="flex space-x-2.5 pt-7">
                    <Button
                      onClick={() => {
                        if (journeyCtx.state.isExternal) {
                          newTaskForExternalUserCtx.dispatch({
                            type: "open_update",
                            projectId: phase._id,
                          });
                        } else {
                          newTaskForInternalUserCtx.dispatch({
                            type: "open_update",
                            projectId: phase._id,
                          });
                        }
                      }}
                      variant={"secondary"}
                    >
                      New Task
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant={"secondary"}>New Stage</Button>
                      </DialogTrigger>
                      <DialogContent
                        className={clsx(
                          "top-[20%] w-full max-w-lg translate-y-0 data-[state=open]:slide-in-from-top-8"
                        )}
                      >
                        <DialogHeader>
                          <DialogTitle>New Stage</DialogTitle>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="space-y-1">
                            <InputHeading heading="Name" />
                            <TextInput
                              className="max-w-[300px]"
                              value={newStageName}
                              onChange={(e) => {
                                setNewStageName(e.target.value);
                              }}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button
                              disabled={newStageName.trim() === ""}
                              onClick={() => {
                                addStageMutation.mutate({
                                  params: {
                                    name: newStageName,
                                    journeyId,
                                  },
                                });
                              }}
                            >
                              Create
                            </Button>
                          </DialogClose>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
              </div>
              <div className="flex">
                <div className="h-20 w-20"></div>
                <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                  <div className="h-full w-full bg-white" />
                </div>
                <div className="h-20 w-20"></div>
              </div>
            </div>
          )}
          <MultiSelectActions
            phase={phase}
            journeyId={journeyId}
            spaceId={phase.spaceId}
          />
        </div>
      )}
    </>
  );
};

function MultiSelectActions({
  phase,
  journeyId,
  spaceId,
}: {
  phase: PhaseData;
  journeyId: string;
  spaceId: string;
}) {
  const numberOfSelectedTasks = useNumberOfSelectedTasks();
  const { active, inactive } = useGetDistribution();
  const { clearSelection } = useSelectionActions();
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [statusSelectOpen, setStatusSelectOpen] = useState(false);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const updateTasks = useUpdatePhaseTasks();
  const selectedTasks = useSelectedTasks();
  const { data: assignees } = useGetProjectMembers(phase._id);
  const statusGroupsCtx = useStatusGroups();

  useEffect(() => {
    if (updateTasks.isSuccess) {
      clearSelection();
    }
  }, [updateTasks.isSuccess]);

  if (!numberOfSelectedTasks) {
    return null;
  }
  const statusGroup = statusGroupsCtx.state.statusGroups?.find(
    (sg) => sg.spaceId === spaceId
  );

  let statuses: {
    [key: string]: string[];
  } = {};

  if (statusGroup) {
    statuses = {
      Backlog: statusGroup.statuses
        .filter((s) => s.parentStatus === "Backlog")
        .map((s) => s.name),
      "To-do": statusGroup.statuses
        .filter((s) => s.parentStatus === "To-do")
        .map((s) => s.name),
      "In Progress": statusGroup.statuses
        .filter((s) => s.parentStatus === "In Progress")
        .map((s) => s.name),
      Completed: statusGroup.statuses
        .filter((s) => s.parentStatus === "Completed")
        .map((s) => s.name),
      Stuck: statusGroup.statuses
        .filter((s) => s.parentStatus === "Stuck")
        .map((s) => s.name),
      Canceled: statusGroup.statuses
        .filter((s) => s.parentStatus === "Canceled")
        .map((s) => s.name),
    };
  }

  // console.log(statuses);

  return (
    <>
      <div className="absolute bottom-12 left-1/2 z-10 flex w-fit -translate-x-1/2 items-center gap-2 rounded-md border-[0.5px] border-gray-200 bg-white p-3 shadow-md">
        {!updateTasks.isLoading && (
          <div className="flex h-8 items-center overflow-hidden rounded-md border border-dashed border-gray-400/70 text-sm">
            <div
              className="inline-flex h-full cursor-pointer items-center border-r border-dashed border-gray-400/70 px-1.5 hover:bg-gray-50"
              onClick={() => {
                clearSelection();
              }}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </div>
            <div className="inline-flex h-full min-w-[88px] cursor-default items-center justify-center px-2.5">
              {numberOfSelectedTasks} selected
            </div>
          </div>
        )}
        {updateTasks.isLoading && (
          <div className="flex items-center space-x-2 text-sm text-gray-700">
            <Loading className="h-4 w-4 border-2" />
            <div>Applying changes...</div>
          </div>
        )}
        {active > 0 && inactive === 0 && !updateTasks.isLoading && (
          <>
            <Popover open={statusSelectOpen} onOpenChange={setStatusSelectOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setStatusSelectOpen(true);
                  }}
                  // onClick={() => {
                  //   setChangeStatusDialogOpen(true);
                  // }}
                  // className="flex h-8 cursor-pointer items-center rounded-md border px-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <div className="mr-1.5 h-3.5 w-3.5 rounded-full border-2 border-gray-400" />
                  Status
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-fit p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search status..." />
                  <CommandList>
                    <CommandEmpty>No results found.</CommandEmpty>
                    <CommandGroup>
                      {Object.entries(statuses).map(([status, statuses]) => (
                        <>
                          {statuses.map((s) => {
                            return (
                              <CommandItem
                                key={status}
                                onSelect={() => {
                                  updateTasks.mutate({
                                    tasks: Array.from(
                                      selectedTasks.entries()
                                    ).map(([taskId]) => ({
                                      _id: taskId,
                                      status: s,
                                      customStatus: status,
                                    })),
                                    metadata: {
                                      phaseId: phase._id,
                                      journeyId,
                                    },
                                  });
                                  setStatusSelectOpen(false);
                                  // clearSelection();
                                }}
                              >
                                {getStatusItem(status as ParentStatus, s)}
                              </CommandItem>
                            );
                          })}
                        </>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDueDateOpen(true);
                  }}
                  // onClick={() => {
                  //   setChangeStatusDialogOpen(true);
                  // }}
                  // className="flex h-8 cursor-pointer items-center rounded-md border px-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                  Due Date
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-fit p-0" align="start">
                <Calendar
                  mode="single"
                  onSelect={(date) => {
                    updateTasks.mutate({
                      tasks: Array.from(selectedTasks.entries()).map(
                        ([taskId]) => ({
                          _id: taskId,
                          dueDate: date?.toISOString(),
                        })
                      ),
                      metadata: {
                        phaseId: phase._id,
                        journeyId,
                      },
                    });
                    setDueDateOpen(false);
                    // clearSelection();
                  }}
                />
              </PopoverContent>
            </Popover>
            <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAssigneeOpen(true);
                  }}
                  // onClick={() => {
                  //   setChangeStatusDialogOpen(true);
                  // }}
                  // className="flex h-8 cursor-pointer items-center rounded-md border px-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <UserIcon className="mr-1.5 h-3.5 w-3.5" />
                  Assignee
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-fit p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search assignee..." />
                  <CommandList>
                    <CommandEmpty>No results found.</CommandEmpty>
                    <CommandGroup heading="Internal">
                      {assignees?.internal.map((a) => {
                        return (
                          <CommandItem
                            key={a._id}
                            onSelect={() => {
                              updateTasks.mutate({
                                tasks: Array.from(selectedTasks.entries()).map(
                                  ([taskId]) => ({
                                    _id: taskId,
                                    taskerId: a._id,
                                    taskerType: "internal",
                                  })
                                ),
                                metadata: {
                                  phaseId: phase._id,
                                  journeyId,
                                },
                              });
                              setAssigneeOpen(false);
                              // clearSelection();
                            }}
                          >
                            {a.name}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                    <CommandGroup heading="External">
                      {assignees?.external.map((a) => {
                        return (
                          <CommandItem
                            key={a.email}
                            onSelect={() => {
                              if (a.userType === "fullUser") {
                                updateTasks.mutate({
                                  tasks: Array.from(
                                    selectedTasks.entries()
                                  ).map(([taskId]) => ({
                                    _id: taskId,
                                    taskerId: a._id,
                                    taskerType: "external",
                                  })),
                                  metadata: {
                                    phaseId: phase._id,
                                    journeyId,
                                  },
                                });
                              } else {
                                updateTasks.mutate({
                                  tasks: Array.from(
                                    selectedTasks.entries()
                                  ).map(([taskId]) => ({
                                    _id: taskId,
                                    taskerEmail: a.email,
                                  })),
                                  metadata: {
                                    phaseId: phase._id,
                                    journeyId,
                                  },
                                });
                              }
                              setAssigneeOpen(false);
                              // clearSelection();
                            }}
                          >
                            {a.userType === "fullUser" ? a.name : a.email}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </>
        )}
        {/* {inactive > 0 && active === 0 && (
          <div
            onClick={() => {
              setChangeStatusDialogOpen(true);
            }}
            className="flex h-8 cursor-pointer items-center rounded-md border px-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
          >
            Activate Tasks
          </div>
        )} */}
        {inactive > 0 && (
          <div
            onClick={() => {
              setChangeStatusDialogOpen(true);
            }}
            className="pl-1 text-sm text-gray-700"
          >
            Please only select active tasks for bulk actions.
          </div>
        )}
      </div>
      {/* <Dialog
        open={changeStatusDialogOpen}
        onOpenChange={setChangeStatusDialogOpen}
      >
        <DialogContent>
          <div>{numberOfSelectedTasks} selected.</div>
        </DialogContent>
      </Dialog> */}
    </>
  );
}

type Logic = "AND" | "OR";

export type FilterGroup = {
  logic: Logic;
  children: Filter[];
};

type IdComparator = "eq" | "in" | "notIn";

type DateComparator = "eq" | "gt" | "gte" | "lt" | "lte";

type StateComparator = "is";

// type Comparator = {
//   boolean: {
//     eq: boolean | null;
//   };
//   string: {
//     in?: string[];
//     notIn?: string[];
//     eq?: string | null;
//   };
//   number: {
//     eq?: number | null;
//     gt?: number | null;
//     gte?: number | null;
//     lt?: number | null;
//     lte?: number | null;
//     in?: number[];
//   };
//   date: {
//     eq?: string | null;
//     gt?: string | null;
//     gte?: string | null;
//     lt?: string | null;
//     lte?: string | null;
//   };
// };

type StatusFilter = {
  field: "status";
  comparator: IdComparator;
  value: string[];
};

type DueDateFilter = {
  field: "dueDate";
  comparator: DateComparator;
  value: string | null;
};

type StateFilter = {
  field: "state";
  comparator: StateComparator;
  value: string | null;
};

type CompanyFilter = {
  field: "company";
  value: boolean | null; // true = internal, false = external
};

type AssigneeFilter = {
  field: "assignee";
  comparator: IdComparator;
  value: string[];
};

type PriorityFilter = {
  field: "task-priority";
  comparator: IdComparator;
  value: string[];
};

export type Filter =
  | StatusFilter
  | DueDateFilter
  | StateFilter
  | CompanyFilter
  | {
      field: undefined;
      comparator: null;
      value: null;
    }
  | AssigneeFilter
  | PriorityFilter;

function Filter({
  filterModels,
  filterGroup,
  setFilterGroup,
}: {
  filterModels: FilterModel[];
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
}) {
  const [open, setOpen] = useState(false);
  const journeyCtx = useJourney();
  // const { phaseId } = useParams();
  // const { sharedSpace } = useGetSharedSpace(new ObjectID(sharedSpaceId));
  // const { data: phase } = useGetPhase(String(phaseId));
  // get search params
  // const appliedFiltersCount =
  //   (journeyCtx.state.view.responsibility !== "all" ? 1 : 0) +
  //   (journeyCtx.state.view.showOnlyActiveAndIncompleteTasks ? 1 : 0);
  // const [customFilterOpen, setCustomFilterOpen] = useState(false);

  // const [filterGroup, setFilterGroup] = useState<FilterGroup>({
  //   logic: "AND",
  //   children: [
  //     {
  //       field: "",
  //       comparator: {
  //         string: {
  //           in: [""],
  //         },
  //       },
  //     },
  //   ],
  // });

  const [filterCount, setFilterCount] = useState(0);

  useEffect(() => {
    // count the number of valid filters
    let count = 0;
    for (let i = 0; i < filterGroup.children.length; i++) {
      const child = filterGroup.children[i] as Filter;
      if (child.field === "status") {
        if (child.value.length > 0) {
          count++;
        }
      } else if (child.field === "dueDate") {
        if (child.value) {
          count++;
        }
      } else if (child.field === "state") {
        if (child.value) {
          count++;
        }
      } else if (child.field === "company") {
        if (child.value !== null) {
          count++;
        }
      } else if (child.field === "task-priority") {
        if (child.value !== null) {
          count++;
        }
      }
    }
    setFilterCount(count);
  }, [filterGroup]);

  useEffect(() => {
    if (!open) {
      // remove invalid filters
      setFilterGroup({
        ...filterGroup,
        children: filterGroup.children.filter((child) => {
          const currentChild = child;
          if (currentChild.field === "status") {
            return currentChild.value.length > 0;
          } else if (currentChild.field === "dueDate") {
            return !!currentChild.value;
          } else if (currentChild.field === "task-priority") {
            return !!currentChild.value;
          } else if (!currentChild.field) {
            return false;
          }
          return true;
        }),
      });
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary">
          <FunnelIcon className="mr-2 h-3.5 w-3.5 border-gray-300" />
          Filter
          {filterCount > 0 && (
            <div className="ml-2">
              <Count variant={"primaryLight"}>{filterCount}</Count>
            </div>
          )}
          {filterCount > 0 && (
            <button
              tabIndex={-1}
              className="ml-1.5 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
              onClick={(e) => {
                e.stopPropagation();
                setFilterGroup({
                  logic: "AND",
                  children: [
                    // {
                    //   field: undefined,
                    //   comparator: null,
                    //   value: null,
                    // },
                  ],
                });
              }}
            >
              <XMarkIcon className="h-2.5 w-2.5 " />
            </button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-40 w-full min-w-[480px] space-y-3 p-5"
      >
        {/* <div>
          <div>Quick Filters</div>
        </div> */}
        {/* <div>
          Custom
        </div> */}
        <FilterGroup
          filterGroup={filterGroup}
          setFilterGroup={setFilterGroup}
          level={0}
          // phase={phase}
          filterModels={filterModels}
        />

        {/* {!!phase && (
          <TaskerFilter
            customerName={phase.customer?.name}
            instanceName={phase.instance?.name}
          />
        )}
        <TasksFilter /> */}
      </PopoverContent>
    </Popover>
  );
}

type FilterModel =
  | StatusFilterModel
  | DueDateFilterModel
  | StateModel
  | CompanyModel
  | AssigneeModel
  | PriorityFilterModel;

type PriorityFilterModel = {
  field: "task-priority";
};

type StatusFilterModel = {
  field: "status";
  statuses: StatusData[];
};

type DueDateFilterModel = {
  field: "dueDate";
};

type StateModel = {
  field: "state";
};

type AssigneeModel = {
  field: "assignee";
};

type ProjectTemplateModel = {
  field: "projectTemplate";
};

type TaskerTemplateModel = {
  field: "taskerTemplate";
  taskers: {
    _id: string;
    name: string;
    email: string;
  }[];
};

type CompanyModel = {
  field: "company";
  internalCompany: string;
  externalCompany: string;
};

function FilterGroup({
  filterGroup,
  setFilterGroup,
  level,
  filterModels,
}: // phase,
{
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
  level: number;
  // phase: PhaseData;
  filterModels: FilterModel[];
}) {
  const handleLogicChange = (logic: Logic) => {
    setFilterGroup({ ...filterGroup, logic });
  };

  // const handleChildGroupChange = (index: number, childGroup: FilterGroup) => {
  //   const newChildren = [...filterGroup.children];
  //   if ("logic" in newChildren[index]) {
  //     newChildren[index] = childGroup;
  //   }
  //   setFilterGroup({ ...filterGroup, children: newChildren });
  // };

  const addFilter = (field: Filter["field"]) => {
    if (field === "status") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "in",
            value: [],
          },
        ],
      });
    } else if (field === "dueDate") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "lte",
            value: null,
          },
        ],
      });
    } else if (field === "state") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "is",
            value: null,
          },
        ],
      });
    } else if (field === "company") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            value: null,
          },
        ],
      });
    } else if (field === "assignee") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "in",
            value: [],
          },
        ],
      });
    } else if (field === "task-priority") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "in",
            value: [],
          },
        ],
      });
    }
    // setFilterGroup({
    //   ...filterGroup,
    //   children: [
    //     ...filterGroup.children,
    //     {
    //       field: undefined,
    //       comparator: null,
    //       value: null,
    //     },
    //   ],
    // });
  };

  // const addFilterGroup = () => {
  //   setFilterGroup({
  //     ...filterGroup,
  //     children: [...filterGroup.children, { logic: "AND", children: [] }],
  //   });
  // };

  const [open, setOpen] = useState(filterGroup.children.length === 0);

  return (
    <div className={clsx(level > 0 && "rounded-md border p-4")}>
      <div>
        <div className="flex items-start space-x-3">
          <div className="space-y-0.5">
            <div className="inline-flex h-8 w-[52px] items-center justify-center text-sm text-gray-500">
              Where
            </div>
            {filterGroup.children.length > 1 && (
              <Select
                value={filterGroup.logic}
                onValueChange={(value) => {
                  handleLogicChange(value as Logic);
                }}
              >
                <SelectTrigger
                  className="w-[52px] justify-center bg-white"
                  showIcon={false}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="AND">AND</SelectItem>
                    <SelectItem value="OR">OR</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
            {/* show uneditable and/or for each of the filters */}
            {Array.from({ length: filterGroup.children.length - 2 }).map(
              (_, index) => (
                <div
                  key={index}
                  className="flex h-8 w-[52px] items-center justify-center text-sm text-gray-400"
                >
                  {filterGroup.logic}
                </div>
              )
            )}
          </div>
          <div className="space-y-0.5">
            {filterGroup.children.map((child, index) =>
              "field" in child ? (
                <>
                  {child.field === "status" && (
                    <StatusField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      filterModels={filterModels}
                      // phase={phase}
                    />
                  )}
                  {child.field === "dueDate" && (
                    <DueDateField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                    />
                  )}
                  {child.field === "state" && (
                    <StateField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                    />
                  )}
                  {child.field === "company" && (
                    <CompanyField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      filterModels={filterModels}
                    />
                  )}
                  {child.field === "task-priority" && (
                    <PriorityField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      filterModels={filterModels}
                    />
                  )}
                  {/* <div className="flex items-start space-x-2">
                    <FilterField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      phase={phase}
                    />
                  </div> */}
                </>
              ) : (
                // <div key={index} style={{ marginLeft: "20px" }}>
                //   <label>
                //     Field:
                //     <input
                //       type="text"
                //       value={child.field}
                //       onChange={(e) => handleFieldChange(index, e.target.value)}
                //     />
                //   </label>
                //   <label>
                //     Comparator:
                //     <input
                //       type="text"
                //       value={JSON.stringify(child.comparator)}
                //       onChange={(e) =>
                //         handleComparatorChange(
                //           index,
                //           JSON.parse(e.target.value)
                //         )
                //       }
                //     />
                //   </label>
                //   <button onClick={() => removeChild(index)}>Remove</button>
                // </div>
                // <div key={index}>
                //   <FilterGroup
                //     filterGroup={child}
                //     setFilterGroup={(updatedChild: FilterGroup) =>
                //       handleChildGroupChange(index, updatedChild)
                //     }
                //     level={level + 1}
                //   />
                //   <button onClick={() => removeChild(index)}>
                //     Remove Group
                //   </button>
                // </div>
                <></>
              )
            )}
            <div className={clsx(filterGroup.children.length > 0 && "pt-2.5")}>
              <DropdownMenu
                open={open}
                onOpenChange={(isOpen) => {
                  setOpen(isOpen);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={"secondary"}
                    className={clsx(
                      filterGroup.children.length === 0 &&
                        "font-normal text-gray-400/70"
                    )}
                  >
                    {filterGroup.children.length > 0
                      ? "Add Filter"
                      : "Select Filter"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuGroup>
                    {filterModels.map((filterModel, index) => {
                      switch (filterModel.field) {
                        case "status":
                          return (
                            <DropdownMenuItem
                              onSelect={() => {
                                addFilter("status");
                                setOpen(false);
                              }}
                            >
                              Status
                            </DropdownMenuItem>
                          );
                        case "dueDate":
                          return (
                            <DropdownMenuItem
                              onSelect={() => {
                                addFilter("dueDate");
                                setOpen(false);
                              }}
                            >
                              Due Date
                            </DropdownMenuItem>
                          );
                        case "state":
                          return (
                            <DropdownMenuItem
                              onSelect={() => {
                                addFilter("state");
                                setOpen(false);
                              }}
                            >
                              State
                            </DropdownMenuItem>
                          );
                        case "company":
                          return (
                            <DropdownMenuItem
                              onSelect={() => {
                                addFilter("company");
                                setOpen(false);
                              }}
                            >
                              Company
                            </DropdownMenuItem>
                          );
                        case "task-priority":
                          return (
                            <DropdownMenuItem
                              onSelect={() => {
                                addFilter("task-priority");
                                setOpen(false);
                              }}
                            >
                              Priority
                            </DropdownMenuItem>
                          );
                        default:
                          return null;
                      }
                    })}
                    {/* <DropdownMenuItem
                      onSelect={() => {
                        addFilter("status");
                        setOpen(false);
                      }}
                    >
                      Status
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        addFilter("dueDate");
                        setOpen(false);
                      }}
                    >
                      Due Date
                    </DropdownMenuItem> */}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* <Select
              value={undefined}
              onValueChange={(value) => {
                addFilter(value as Filter["field"]);
              }}
              open={open}
              onOpenChange={(isOpen) => {
                setOpen(isOpen);
              }}
            >
              <SelectTrigger
                className={clsx(
                  // "flex h-8 w-32 items-center bg-white px-3",
                  "w-fit"
                )}
                showIcon={false}
              >
                {"Add Filter"}
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="dueDate">Due Date</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select> */}
            {/* <Button variant={"secondary"} onClick={addFilter}>
              
            </Button> */}
          </div>
        </div>
      </div>
    </div>
  );
}

{
  /* <div className="flex items-center space-x-3 text-sm">
          <div className="w-12 text-right text-gray-500">Where</div>
          <div className="flex items-center space-x-0 text-sm">
            <Select>
              <SelectTrigger
                className="flex h-7 w-fit items-center rounded-r-none border px-2"
                showIcon={false}
              >
                Status
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="company">Team</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <div className="flex h-7 w-fit items-center border-y px-2">is</div>
            <div className="flex h-7 w-fit items-center rounded-r-md border px-2">
              is
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <div className="w-12 text-right text-gray-500">And</div>
          <div className="flex items-center space-x-1.5 text-sm">
            <div className="flex h-7 w-fit items-center rounded-md border px-2">
              Tasker
            </div>
            <div className="flex h-7 w-fit items-center rounded-md border px-2">
              is not
            </div>
            <div className="flex h-7 w-fit items-center rounded-md border px-2">
              is
            </div>
          </div>
        </div> */
}

// function FilterField({
//   filter,
//   index,
//   filterGroup,
//   setFilterGroup,
//   phase,
// }: {
//   filter: Filter;
//   index: number;
//   filterGroup: FilterGroup;
//   setFilterGroup: (filterGroup: FilterGroup) => void;
//   phase: PhaseData;
// }) {
//   const [open, setOpen] = useState(!filter.field);
//   const [statusOpen, setStatusOpen] = useState(false);
//   const type = filter.field;
//   const statusGroupsCtx = useStatusGroups();
//   const handleFieldChange = (index: number, field: Filter["field"]) => {
//     const currentChild = filterGroup.children[index] as Filter;
//     if (field === currentChild.field) {
//       return;
//     }
//     const newChildren = [...filterGroup.children];
//     // replace the index with new field
//     if (field === "status") {
//       newChildren[index] = {
//         field: field,
//         value: [],
//         comparator: "in",
//       };
//     } else if (field === "dueDate") {
//       newChildren[index] = {
//         field: field,
//         value: null,
//         comparator: "lte",
//       };
//     }
//     setFilterGroup({ ...filterGroup, children: newChildren });
//   };

//   const removeChild = (index: number) => {
//     const newChildren = filterGroup.children.filter((_, i) => i !== index);
//     setFilterGroup({ ...filterGroup, children: newChildren });
//   };

//   const handleComparatorChange = (
//     index: number,
//     // field: Filter["field"],
//     comparator: IdComparator | DateComparator
//   ) => {
//     const newChildren = [...filterGroup.children];
//     let currentChild = newChildren[index] as Filter;

//     if (currentChild.field === "status") {
//       const statusComparator = comparator as IdComparator;
//       currentChild = {
//         ...currentChild,
//         comparator: statusComparator,
//       };
//     } else if (currentChild.field === "dueDate") {
//       const dateComparator = comparator as DateComparator;
//       currentChild = {
//         ...currentChild,
//         comparator: dateComparator,
//       };
//     }
//     newChildren[index] = currentChild;
//     setFilterGroup({ ...filterGroup, children: newChildren });
//   };

//   const handleValueChange = (
//     value: string | number | boolean | null | string[]
//   ) => {
//     if (type === "status") {
//       const newChildren = [...filterGroup.children];
//       const currentChild = newChildren[index] as Filter;
//       if (currentChild.field === "status") {
//         newChildren[index] = {
//           ...currentChild,
//           value: value as string[],
//         };
//       }
//       setFilterGroup({ ...filterGroup, children: newChildren });
//     }
//   };

//   const statusGroup = statusGroupsCtx.state.statusGroups.find(
//     (sg) => sg._id === phase?.statusGroup._id
//   );

//   const statuses = {
//     Backlog:
//       statusGroup?.statuses
//         .filter((s) => s.parentStatus === "Backlog")
//         .map((s) => s.name) ?? [],
//     "To-do":
//       statusGroup?.statuses
//         .filter((s) => s.parentStatus === "To-do")
//         .map((s) => s.name) ?? [],
//     "In Progress":
//       statusGroup?.statuses
//         .filter((s) => s.parentStatus === "In Progress")
//         .map((s) => s.name) ?? [],
//     Stuck:
//       statusGroup?.statuses
//         .filter((s) => s.parentStatus === "Stuck")
//         .map((s) => s.name) ?? [],
//     Completed:
//       statusGroup?.statuses
//         .filter((s) => s.parentStatus === "Completed")
//         .map((s) => s.name) ?? [],
//     Canceled:
//       statusGroup?.statuses
//         .filter((s) => s.parentStatus === "Canceled")
//         .map((s) => s.name) ?? [],
//   };

//   return (
//     <div className="flex items-center space-x-0 text-sm">
//       <Select
//         value={filter.field}
//         onValueChange={(value) => {
//           handleFieldChange(index, value as Filter["field"]);
//           if (value === "status") {
//             setStatusOpen(true);
//           }
//         }}
//         open={open}
//         onOpenChange={(isOpen) => {
//           setOpen(isOpen);
//         }}
//       >
//         <SelectTrigger
//           className={clsx(
//             "flex h-8 w-32 items-center truncate border bg-white px-3",
//             !!filter.field ? "rounded-r-none" : ""
//           )}
//           showIcon={false}
//         >
//           {type === "status" ? "Status" : type === "dueDate" ? "Due Date" : ""}
//         </SelectTrigger>
//         <SelectContent>
//           <SelectGroup>
//             <SelectItem value="status">Status</SelectItem>
//             <SelectItem value="dueDate">Due Date</SelectItem>
//           </SelectGroup>
//         </SelectContent>
//       </Select>

//       {filter.field === "status" ? (
//         <Select
//           value={filter.comparator}
//           onValueChange={(value) => {
//             handleComparatorChange(index, value as IdComparator);
//           }}
//         >
//           <SelectTrigger
//             className="flex h-8 w-20 items-center truncate rounded-none border-y border-x-0 bg-white px-3"
//             showIcon={false}
//           >
//             {filter.comparator === "in"
//               ? "is"
//               : filter.comparator === "notIn"
//               ? "is not"
//               : ""}
//           </SelectTrigger>
//           <SelectContent>
//             <SelectGroup>
//               <SelectItem value="in">is</SelectItem>
//               <SelectItem value="notIn">is not</SelectItem>
//             </SelectGroup>
//           </SelectContent>
//         </Select>
//       ) : filter.field === "dueDate" ? (
//         <Select
//           value={filter.comparator}
//           onValueChange={(value) => {
//             handleComparatorChange(index, value as DateComparator);
//           }}
//         >
//           <SelectTrigger
//             className="flex h-8 w-20 items-center truncate rounded-none border-y border-x-0 bg-white px-3"
//             showIcon={false}
//           >
//             {filter.comparator === "lte"
//               ? "is before"
//               : filter.comparator === "gte"
//               ? "is after"
//               : "s"}
//           </SelectTrigger>
//           <SelectContent>
//             <SelectGroup>
//               <SelectItem value="lte">is before</SelectItem>
//               <SelectItem value="gte">is after</SelectItem>
//             </SelectGroup>
//           </SelectContent>
//         </Select>
//       ) : (
//         <></>
//       )}
//       {filter.field === "status" ? (
//         <Popover modal={true} open={statusOpen} onOpenChange={setStatusOpen}>
//           <PopoverTrigger className="h-8 border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none">
//             {!!filter.value && filter.value.length > 0 ? (
//               <div className="truncate">
//                 {filter.value.length > 1 ? "Multiple" : filter.value[0]}
//               </div>
//             ) : (
//               <div className="text-sm font-normal text-gray-300">
//                 Select Status
//               </div>
//             )}
//           </PopoverTrigger>
//           <PopoverContent
//             className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
//             align="start"
//           >
//             <Command>
//               <CommandInput placeholder="Search status..." className="h-9" />
//               <CommandEmpty>No status found.</CommandEmpty>
//               <CommandGroup>
//                                 {statuses["Backlog"]?.map((customStatus) => (
//                   <StatusCommandItem
//                     parentStatus={"Backlog"}
//                     key={customStatus}
//                     customStatus={customStatus}
//                     checked={filter.value.includes(customStatus)}
//                     onSelect={(customStatus) => {
//                       if (filter.value.includes(customStatus)) {
//                         handleValueChange(
//                           filter.value.filter((v) => v !== customStatus)
//                         );
//                       } else {
//                         handleValueChange([...filter.value, customStatus]);
//                       }
//                     }}
//                   />
//                 ))}
//                 {statuses["To-do"]?.map((customStatus) => (
//                   <StatusCommandItem
//                     parentStatus={"To-do"}
//                     key={customStatus}
//                     customStatus={customStatus}
//                     checked={filter.value.includes(customStatus)}
//                     onSelect={(customStatus) => {
//                       if (filter.value.includes(customStatus)) {
//                         handleValueChange(
//                           filter.value.filter((v) => v !== customStatus)
//                         );
//                       } else {
//                         handleValueChange([...filter.value, customStatus]);
//                       }
//                     }}
//                   />
//                 ))}
//                 {statuses["In Progress"]?.map((customStatus) => (
//                   <StatusCommandItem
//                     parentStatus={"In Progress"}
//                     key={customStatus}
//                     customStatus={customStatus}
//                     checked={filter.value.includes(customStatus)}
//                     onSelect={(customStatus) => {
//                       if (filter.value.includes(customStatus)) {
//                         handleValueChange(
//                           filter.value.filter((v) => v !== customStatus)
//                         );
//                       } else {
//                         handleValueChange([...filter.value, customStatus]);
//                       }
//                     }}
//                   />
//                 ))}
//                 {statuses["Stuck"]?.map((customStatus) => (
//                   <StatusCommandItem
//                     parentStatus={"Stuck"}
//                     key={customStatus}
//                     customStatus={customStatus}
//                     checked={filter.value.includes(customStatus)}
//                     onSelect={(customStatus) => {
//                       if (filter.value.includes(customStatus)) {
//                         handleValueChange(
//                           filter.value.filter((v) => v !== customStatus)
//                         );
//                       } else {
//                         handleValueChange([...filter.value, customStatus]);
//                       }
//                     }}
//                   />
//                 ))}
//                 {statuses["Completed"]?.map((customStatus) => (
//                   <StatusCommandItem
//                     parentStatus={"Completed"}
//                     key={customStatus}
//                     customStatus={customStatus}
//                     checked={filter.value.includes(customStatus)}
//                     onSelect={(customStatus) => {
//                       if (filter.value.includes(customStatus)) {
//                         handleValueChange(
//                           filter.value.filter((v) => v !== customStatus)
//                         );
//                       } else {
//                         handleValueChange([...filter.value, customStatus]);
//                       }
//                     }}
//                   />
//                 ))}
//                 {statuses["Canceled"]?.map((customStatus) => (
//                   <StatusCommandItem
//                     parentStatus={"Canceled"}
//                     key={customStatus}
//                     customStatus={customStatus}
//                     checked={filter.value.includes(customStatus)}
//                     onSelect={(customStatus) => {
//                       if (filter.value.includes(customStatus)) {
//                         handleValueChange(
//                           filter.value.filter((v) => v !== customStatus)
//                         );
//                       } else {
//                         handleValueChange([...filter.value, customStatus]);
//                       }
//                     }}
//                   />
//                 ))}
//               </CommandGroup>
//             </Command>
//           </PopoverContent>
//         </Popover>
//       ) : (
//         <></>
//       )}

//       {filter.field === "status" && (
//         <div
//           className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-1.5 transition-colors hover:bg-gray-100"
//           onClick={() => {
//             removeChild(index);
//           }}
//         >
//           <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
//         </div>
//       )}
//     </div>
//   );
// }

function CompanyField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
  filterModels,
}: {
  filter: CompanyFilter;
  index: number;
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
  filterModels: FilterModel[];
}) {
  const [open, setOpen] = useState(filter.value === null);
  const companyFilterModel = filterModels.find(
    (fm) => fm.field === "company"
  ) as CompanyModel | undefined;
  const internalCompany = companyFilterModel?.internalCompany;
  const externalCompany = companyFilterModel?.externalCompany;

  const handleValueChange = (value: boolean) => {
    const newChildren = [...filterGroup.children];
    const currentChild = newChildren[index];
    if (currentChild.field === "company") {
      newChildren[index] = {
        ...currentChild,
        value: value,
      };
    }
    setFilterGroup({ ...filterGroup, children: newChildren });
  };
  const removeChild = () => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        Company
      </div>
      <div className="flex h-8 w-20 items-center truncate border-y border-input px-3 text-sm">
        is
      </div>
      <Select
        value={filter.value === null ? "" : filter.value ? "true" : "false"}
        onValueChange={(value) => {
          handleValueChange(value === "true");
        }}
        open={open}
        onOpenChange={setOpen}
      >
        <SelectTrigger
          className="flex h-8 w-40 items-center truncate rounded-none border-y border-l border-r-0 border-input bg-white px-3"
          showIcon={false}
        >
          {filter.value === null ? (
            <span className="text-gray-400/70">Select State</span>
          ) : !!filter.value ? (
            <div className="truncate">{internalCompany}</div>
          ) : (
            <div className="truncate">{externalCompany}</div>
          )}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="true">{internalCompany}</SelectItem>
            <SelectItem value="false">{externalCompany}</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild();
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function StateField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
}: {
  filter: StateFilter;
  index: number;
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
}) {
  const [open, setOpen] = useState(!filter.value);
  const handleValueChange = (value: string) => {
    const newChildren = [...filterGroup.children];
    const currentChild = newChildren[index] as Filter;
    if (currentChild.field === "state") {
      newChildren[index] = {
        ...currentChild,
        value: value,
      };
    }
    setFilterGroup({ ...filterGroup, children: newChildren });
  };
  const removeChild = () => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };
  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        State
      </div>
      <div className="flex h-8 w-20 items-center truncate border-y border-input px-3 text-sm">
        is
      </div>
      <Select
        value={filter.value ?? ""}
        onValueChange={(value) => {
          handleValueChange(value);
        }}
        open={open}
        onOpenChange={setOpen}
      >
        <SelectTrigger
          className="flex h-8 w-40 items-center truncate rounded-none border-y border-l border-r-0 border-input bg-white px-3"
          showIcon={false}
        >
          {filter.value === "active" ? (
            "active"
          ) : filter.value === "inactive" ? (
            "active"
          ) : (
            <span className="text-gray-400/70">Select State</span>
          )}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="active">active</SelectItem>
            <SelectItem value="inactive">inactive</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild();
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function StatusField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
  filterModels,
}: {
  filter: StatusFilter;
  index: number;
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
  filterModels: FilterModel[];
}) {
  const [open, setOpen] = useState(!filter.field);
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);
  const statusFilterModel = filterModels.find((fm) => fm.field === "status");
  if (!statusFilterModel) return null;
  const statuses = (statusFilterModel as StatusFilterModel).statuses;
  const type = filter.field;

  const handleFieldChange = (index: number, field: Filter["field"]) => {
    const currentChild = filterGroup.children[index] as Filter;
    if (field === currentChild.field) {
      return;
    }
    const newChildren = [...filterGroup.children];
    // replace the index with new field
    if (field === "status") {
      newChildren[index] = {
        field: field,
        value: [],
        comparator: "in",
      };
    } else if (field === "dueDate") {
      newChildren[index] = {
        field: field,
        value: null,
        comparator: "lte",
      };
    }
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const removeChild = (index: number) => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: IdComparator) => {
    const newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as Filter;

    if (currentChild.field === "status") {
      const statusComparator = comparator as IdComparator;
      currentChild = {
        ...currentChild,
        comparator: statusComparator,
      };
    }
    newChildren[index] = currentChild;
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleValueChange = (
    value: string | number | boolean | null | string[]
  ) => {
    if (type === "status") {
      const newChildren = [...filterGroup.children];
      const currentChild = newChildren[index] as Filter;
      if (currentChild.field === "status") {
        newChildren[index] = {
          ...currentChild,
          value: value as string[],
        };
      }
      setFilterGroup({ ...filterGroup, children: newChildren });
    }
  };

  const transforemdStatuses = {
    Backlog:
      statuses.filter((s) => s.parentStatus === "Backlog").map((s) => s.name) ??
      [],
    "To-do":
      statuses.filter((s) => s.parentStatus === "To-do").map((s) => s.name) ??
      [],
    "In Progress":
      statuses
        .filter((s) => s.parentStatus === "In Progress")
        .map((s) => s.name) ?? [],
    Stuck:
      statuses.filter((s) => s.parentStatus === "Stuck").map((s) => s.name) ??
      [],
    Completed:
      statuses
        .filter((s) => s.parentStatus === "Completed")
        .map((s) => s.name) ?? [],
    Canceled:
      statuses
        .filter((s) => s.parentStatus === "Canceled")
        .map((s) => s.name) ?? [],
  };

  return (
    <div className="flex items-center space-x-0 text-sm">
      {/* <Select
        value={filter.field}
        onValueChange={(value) => {
          handleFieldChange(index, value as Filter["field"]);
          if (value === "status") {
            setValueOpen(true);
          }
        }}
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
        }}
      >
        <SelectTrigger
          className={clsx(
            "flex h-8 w-32 items-center truncate border bg-white px-3",
            !!filter.field ? "rounded-r-none" : ""
          )}
          showIcon={false}
        >
          {type === "status" ? "Status" : type === "dueDate" ? "Due Date" : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="status">Status</SelectItem>
            <SelectItem value="dueDate">Due Date</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select> */}
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        Status
      </div>
      <Select
        value={filter.comparator}
        onValueChange={(value) => {
          handleComparatorChange(value as IdComparator);
        }}
      >
        <SelectTrigger
          className="flex h-8 w-20 items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3"
          showIcon={false}
        >
          {filter.comparator === "in"
            ? "is"
            : filter.comparator === "notIn"
            ? "is not"
            : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="in">is</SelectItem>
            <SelectItem value="notIn">is not</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger className="flex h-8 w-40 items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none">
          {!!filter.value && filter.value.length > 0 ? (
            <div className="truncate">
              {filter.value.length > 1 ? "Multiple" : filter.value[0]}
            </div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Status
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search status..." className="h-9" />
            <CommandList>
              <CommandEmpty>No status found.</CommandEmpty>
              <CommandGroup>
                {transforemdStatuses["Backlog"]?.map((customStatus) => (
                  <StatusCommandItem
                    parentStatus={"Backlog"}
                    key={customStatus}
                    customStatus={customStatus}
                    checked={filter.value.includes(customStatus)}
                    onSelect={(customStatus) => {
                      if (filter.value.includes(customStatus)) {
                        handleValueChange(
                          filter.value.filter((v) => v !== customStatus)
                        );
                      } else {
                        handleValueChange([...filter.value, customStatus]);
                      }
                    }}
                  />
                ))}
                {transforemdStatuses["To-do"]?.map((customStatus) => (
                  <StatusCommandItem
                    parentStatus={"To-do"}
                    key={customStatus}
                    customStatus={customStatus}
                    checked={filter.value.includes(customStatus)}
                    onSelect={(customStatus) => {
                      if (filter.value.includes(customStatus)) {
                        handleValueChange(
                          filter.value.filter((v) => v !== customStatus)
                        );
                      } else {
                        handleValueChange([...filter.value, customStatus]);
                      }
                    }}
                  />
                ))}
                {transforemdStatuses["In Progress"]?.map((customStatus) => (
                  <StatusCommandItem
                    parentStatus={"In Progress"}
                    key={customStatus}
                    customStatus={customStatus}
                    checked={filter.value.includes(customStatus)}
                    onSelect={(customStatus) => {
                      if (filter.value.includes(customStatus)) {
                        handleValueChange(
                          filter.value.filter((v) => v !== customStatus)
                        );
                      } else {
                        handleValueChange([...filter.value, customStatus]);
                      }
                    }}
                  />
                ))}
                {transforemdStatuses["Stuck"]?.map((customStatus) => (
                  <StatusCommandItem
                    parentStatus={"Stuck"}
                    key={customStatus}
                    customStatus={customStatus}
                    checked={filter.value.includes(customStatus)}
                    onSelect={(customStatus) => {
                      if (filter.value.includes(customStatus)) {
                        handleValueChange(
                          filter.value.filter((v) => v !== customStatus)
                        );
                      } else {
                        handleValueChange([...filter.value, customStatus]);
                      }
                    }}
                  />
                ))}
                {transforemdStatuses["Completed"]?.map((customStatus) => (
                  <StatusCommandItem
                    parentStatus={"Completed"}
                    key={customStatus}
                    customStatus={customStatus}
                    checked={filter.value.includes(customStatus)}
                    onSelect={(customStatus) => {
                      if (filter.value.includes(customStatus)) {
                        handleValueChange(
                          filter.value.filter((v) => v !== customStatus)
                        );
                      } else {
                        handleValueChange([...filter.value, customStatus]);
                      }
                    }}
                  />
                ))}
                {transforemdStatuses["Canceled"]?.map((customStatus) => (
                  <StatusCommandItem
                    parentStatus={"Canceled"}
                    key={customStatus}
                    customStatus={customStatus}
                    checked={filter.value.includes(customStatus)}
                    onSelect={(customStatus) => {
                      if (filter.value.includes(customStatus)) {
                        handleValueChange(
                          filter.value.filter((v) => v !== customStatus)
                        );
                      } else {
                        handleValueChange([...filter.value, customStatus]);
                      }
                    }}
                  />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild(index);
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function PriorityField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
  filterModels,
}: {
  filter: PriorityFilter;
  index: number;
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
  filterModels: FilterModel[];
}) {
  const [open, setOpen] = useState(!filter.field);
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);
  const priorityFilterModel = filterModels.find(
    (fm) => fm.field === "task-priority"
  );
  if (!priorityFilterModel) return null;
  const priorities: PriorityData[] = ["low", "medium", "high", "urgent"];
  const type = filter.field;

  // const handleFieldChange = (index: number, field: Filter["field"]) => {
  //   const currentChild = filterGroup.children[index] as Filter;
  //   if (field === currentChild.field) {
  //     return;
  //   }
  //   const newChildren = [...filterGroup.children];
  //   // replace the index with new field
  //   if (field === "status") {
  //     newChildren[index] = {
  //       field: field,
  //       value: [],
  //       comparator: "in",
  //     };
  //   } else if (field === "dueDate") {
  //     newChildren[index] = {
  //       field: field,
  //       value: null,
  //       comparator: "lte",
  //     };
  //   }
  //   setFilterGroup({ ...filterGroup, children: newChildren });
  // };

  const removeChild = (index: number) => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: IdComparator) => {
    const newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as Filter;

    if (currentChild.field === "task-priority") {
      const priorityComparator = comparator as IdComparator;
      currentChild = {
        ...currentChild,
        comparator: priorityComparator,
      };
    }
    newChildren[index] = currentChild;
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleValueChange = (
    value: string | number | boolean | null | string[]
  ) => {
    if (type === "task-priority") {
      const newChildren = [...filterGroup.children];
      const currentChild = newChildren[index] as Filter;
      if (currentChild.field === "task-priority") {
        newChildren[index] = {
          ...currentChild,
          value: value as string[],
        };
      }
      setFilterGroup({ ...filterGroup, children: newChildren });
    }
  };
  return (
    <div className="flex items-center space-x-0 text-sm">
      {/* <Select
        value={filter.field}
        onValueChange={(value) => {
          handleFieldChange(index, value as Filter["field"]);
          if (value === "status") {
            setValueOpen(true);
          }
        }}
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
        }}
      >
        <SelectTrigger
          className={clsx(
            "flex h-8 w-32 items-center truncate border bg-white px-3",
            !!filter.field ? "rounded-r-none" : ""
          )}
          showIcon={false}
        >
          {type === "status" ? "Status" : type === "dueDate" ? "Due Date" : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="status">Status</SelectItem>
            <SelectItem value="dueDate">Due Date</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select> */}
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        Priority
      </div>
      <Select
        value={filter.comparator}
        onValueChange={(value) => {
          handleComparatorChange(value as IdComparator);
        }}
      >
        <SelectTrigger
          className="flex h-8 w-20 items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3"
          showIcon={false}
        >
          {filter.comparator === "in"
            ? "is"
            : filter.comparator === "notIn"
            ? "is not"
            : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="in">is</SelectItem>
            <SelectItem value="notIn">is not</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger className="flex h-8 w-40 items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none">
          {!!filter.value && filter.value.length > 0 ? (
            <div className="truncate">
              {filter.value.length > 1 ? "Multiple" : filter.value[0]}
            </div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Priority
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search priority..." className="h-9" />
            <CommandList>
              <CommandEmpty>No priority found.</CommandEmpty>
              <CommandGroup>
                {priorities.map((priority) => (
                  <CommandItem
                    key={priority}
                    onSelect={() => {
                      if (filter.value.includes(priority)) {
                        handleValueChange(
                          filter.value.filter((v) => v !== priority)
                        );
                      } else {
                        handleValueChange([...filter.value, priority]);
                      }
                    }}
                    className="gap-2"
                  >
                    <Checkbox
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      checked={filter.value.includes(priority)}
                      onCheckedChange={(_) => {
                        if (filter.value.includes(priority)) {
                          handleValueChange(
                            filter.value.filter((v) => v !== priority)
                          );
                        } else {
                          handleValueChange([...filter.value, priority]);
                        }
                      }}
                    />

                    {getPriorityIconAndLabel(priority)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild(index);
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function DueDateField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
}: {
  filter: DueDateFilter;
  index: number;
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
}) {
  const [open, setOpen] = useState(!filter.field);
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);
  const statusGroupsCtx = useStatusGroups();
  const handleFieldChange = (field: Filter["field"]) => {
    const currentChild = filterGroup.children[index] as Filter;
    if (field === currentChild.field) {
      return;
    }
    const newChildren = [...filterGroup.children];
    // replace the index with new field
    if (field === "status") {
      newChildren[index] = {
        field: field,
        value: [],
        comparator: "in",
      };
    } else if (field === "dueDate") {
      newChildren[index] = {
        field: field,
        value: null,
        comparator: "lte",
      };
    }
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const removeChild = (index: number) => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: DateComparator) => {
    const newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as Filter;

    if (currentChild.field === "dueDate") {
      currentChild = {
        ...currentChild,
        comparator,
      };
    }
    newChildren[index] = currentChild;
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleValueChange = (
    value: string | number | boolean | null | string[]
  ) => {
    const newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as DueDateFilter;
    if (currentChild.field === "dueDate") {
      newChildren[index] = {
        ...currentChild,
        value: value as string | null,
      };
      setFilterGroup({ ...filterGroup, children: newChildren });
    }
  };

  return (
    <div className="flex items-center space-x-0 text-sm">
      <Select
        value={filter.field}
        onValueChange={(value) => {
          handleFieldChange(value as Filter["field"]);
          if (value === "dueDate") {
            setValueOpen(true);
          }
        }}
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
        }}
      >
        <SelectTrigger
          className={clsx(
            "flex h-8 w-32 items-center truncate border bg-white px-3",
            !!filter.field ? "rounded-r-none" : ""
          )}
          showIcon={false}
        >
          {"Due Date"}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="status">Status</SelectItem>
            <SelectItem value="dueDate">Due Date</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      <Select
        value={filter.comparator}
        onValueChange={(value) => {
          handleComparatorChange(value as DateComparator);
        }}
      >
        <SelectTrigger
          className="flex h-8 w-20 items-center truncate rounded-none border-y border-x-0 bg-white px-3"
          showIcon={false}
        >
          {filter.comparator === "lte"
            ? "is before"
            : filter.comparator === "gte"
            ? "is after"
            : "s"}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="lte">is before</SelectItem>
            <SelectItem value="gte">is after</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      <Select
        value={filter.value ?? ""}
        onValueChange={(value) => {
          handleValueChange(value);
        }}
        open={valueOpen}
        onOpenChange={setValueOpen}
      >
        <SelectTrigger
          className="flex h-8 w-40 items-center truncate rounded-none border-r-0 bg-white px-3"
          showIcon={false}
        >
          {filter.value ?? <div className="text-gray-400/70">Select Date</div>}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="end of today">end of today</SelectItem>
            <SelectItem value="end of tomorrow">end of tomorrow</SelectItem>
            <SelectItem value="end of week">end of week</SelectItem>
            <SelectItem value="end of month">end of month</SelectItem>
            {/* <SelectItem value="custom"></SelectItem> */}
          </SelectGroup>
        </SelectContent>
      </Select>

      {/* <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger className="h-8 border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none">
          {!!filter.value && filter.value.length > 0 ? (
            <div className="truncate">
              {filter.value.length > 1 ? "Multiple" : filter.value[0]}
            </div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Date
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search status..." className="h-9" />
            <CommandEmpty>No status found.</CommandEmpty>
            <CommandGroup>
              {statuses["Backlog"]?.map((customStatus) => (
                <StatusCommandItem
                  parentStatus={"Backlog"}
                  key={customStatus}
                  customStatus={customStatus}
                  checked={filter.value.includes(customStatus)}
                  onSelect={(customStatus) => {
                    if (filter.value.includes(customStatus)) {
                      handleValueChange(
                        filter.value.filter((v) => v !== customStatus)
                      );
                    } else {
                      handleValueChange([...filter.value, customStatus]);
                    }
                  }}
                />
              ))}
              {statuses["To-do"]?.map((customStatus) => (
                <StatusCommandItem
                  parentStatus={"To-do"}
                  key={customStatus}
                  customStatus={customStatus}
                  checked={filter.value.includes(customStatus)}
                  onSelect={(customStatus) => {
                    if (filter.value.includes(customStatus)) {
                      handleValueChange(
                        filter.value.filter((v) => v !== customStatus)
                      );
                    } else {
                      handleValueChange([...filter.value, customStatus]);
                    }
                  }}
                />
              ))}
              {statuses["In Progress"]?.map((customStatus) => (
                <StatusCommandItem
                  parentStatus={"In Progress"}
                  key={customStatus}
                  customStatus={customStatus}
                  checked={filter.value.includes(customStatus)}
                  onSelect={(customStatus) => {
                    if (filter.value.includes(customStatus)) {
                      handleValueChange(
                        filter.value.filter((v) => v !== customStatus)
                      );
                    } else {
                      handleValueChange([...filter.value, customStatus]);
                    }
                  }}
                />
              ))}
              {statuses["Stuck"]?.map((customStatus) => (
                <StatusCommandItem
                  parentStatus={"Stuck"}
                  key={customStatus}
                  customStatus={customStatus}
                  checked={filter.value.includes(customStatus)}
                  onSelect={(customStatus) => {
                    if (filter.value.includes(customStatus)) {
                      handleValueChange(
                        filter.value.filter((v) => v !== customStatus)
                      );
                    } else {
                      handleValueChange([...filter.value, customStatus]);
                    }
                  }}
                />
              ))}
              {statuses["Completed"]?.map((customStatus) => (
                <StatusCommandItem
                  parentStatus={"Completed"}
                  key={customStatus}
                  customStatus={customStatus}
                  checked={filter.value.includes(customStatus)}
                  onSelect={(customStatus) => {
                    if (filter.value.includes(customStatus)) {
                      handleValueChange(
                        filter.value.filter((v) => v !== customStatus)
                      );
                    } else {
                      handleValueChange([...filter.value, customStatus]);
                    }
                  }}
                />
              ))}
              {statuses["Canceled"]?.map((customStatus) => (
                <StatusCommandItem
                  parentStatus={"Canceled"}
                  key={customStatus}
                  customStatus={customStatus}
                  checked={filter.value.includes(customStatus)}
                  onSelect={(customStatus) => {
                    if (filter.value.includes(customStatus)) {
                      handleValueChange(
                        filter.value.filter((v) => v !== customStatus)
                      );
                    } else {
                      handleValueChange([...filter.value, customStatus]);
                    }
                  }}
                />
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover> */}

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild(index);
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function StatusCommandItem({
  parentStatus,
  customStatus,
  onSelect,
  checked,
}: {
  parentStatus: ParentStatus;
  customStatus: string;
  onSelect: (customStatus: string) => void;
  checked: boolean;
}) {
  return (
    <CommandItem
      key={customStatus}
      onSelect={() => {
        onSelect(customStatus);
      }}
      className="gap-2"
    >
      <Checkbox
        onClick={(e) => {
          e.stopPropagation();
        }}
        checked={checked}
        onCheckedChange={(_) => {
          onSelect(customStatus);
        }}
      />

      <div className="flex items-center space-x-2">
        {getStatusSymbol(parentStatus, false)}
        <span className="truncate">{customStatus}</span>
      </div>
    </CommandItem>
  );
}

function FieldComparator({
  filter,
  index,
  filterGroup,
  setFilterGroup,
}: {
  filter: Filter;
  index: number;
  filterGroup: FilterGroup;
  setFilterGroup: (filterGroup: FilterGroup) => void;
}) {}

function Display() {
  const [open, setOpen] = useState(false);
  const journeyCtx = useJourney();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary">
          <AdjustmentsHorizontalIcon className="mr-1.5 h-3.5 w-4" />
          Display
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0">
        <div className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm">Layout</div>
            <div className="inline-flex cursor-pointer items-center rounded-lg">
              <button
                className={clsx(
                  "rounded-l-md border py-1 px-2.5",
                  journeyCtx.state.view.view === "kanban"
                    ? "border-gray-400 bg-gray-100"
                    : "border-r-0 text-gray-400 hover:bg-gray-50"
                )}
                onClick={() => {
                  journeyCtx.dispatch({
                    type: "VIEW_VIEW_UPDATE",
                    view: "kanban",
                  });
                }}
              >
                <ViewColumnsIcon className={clsx("h-5 w-5")} />
              </button>
              {/* <button
                className={clsx(
                  "py-1 px-2.5",
                  journeyCtx.state.view.view === "gantt"
                    ? "border border-gray-400 bg-gray-100"
                    : // ? journeyCtx.state.isExternal
                      //   ? "border-r border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                      //   : "border-r border-primary-main bg-primary-100 text-primary-main"
                      "border-y text-gray-400 hover:bg-gray-50"
                )}
                onClick={() => {
                  journeyCtx.dispatch({
                    type: "VIEW_VIEW_UPDATE",
                    view: "gantt",
                  });
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={clsx(
                    "h-5 w-5",
                    journeyCtx.state.view.view === "gantt"
                      ? "stroke-black"
                      : "stroke-gray-500"
                  )}
                >
                  <rect
                    x="2"
                    y="4"
                    width="10"
                    height="4"
                    rx="1"
                    strokeWidth="1"
                  />
                  <rect
                    x="8"
                    y="10"
                    width="13"
                    height="4"
                    rx="1"
                    strokeWidth="1"
                  />
                  <rect
                    x="4"
                    y="16"
                    width="12"
                    height="4"
                    rx="1"
                    strokeWidth="1"
                  />
                </svg>
              </button> */}
              <button
                className={clsx(
                  "rounded-r-md border py-1 px-2.5",
                  journeyCtx.state.view.view === "list"
                    ? "border-gray-400 bg-gray-100"
                    : // ? journeyCtx.state.isExternal
                      //   ? "border-l border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                      //   : "border-l border-primary-main bg-primary-100 text-primary-main"
                      "text-gray-400 hover:bg-gray-50",
                  journeyCtx.state.view.view === "gantt" && "border-l-0"
                )}
                onClick={() => {
                  journeyCtx.dispatch({
                    type: "VIEW_VIEW_UPDATE",
                    view: "list",
                  });
                }}
              >
                <ListBulletIcon className={clsx("h-5 w-5")} />
              </button>
            </div>
          </div>
          {journeyCtx.state.view.view === "kanban" && (
            <div className="flex items-center justify-between">
              <div className="text-sm">Group by</div>
              <Select
                value={journeyCtx.state.group}
                onValueChange={(value) => {
                  journeyCtx.dispatch({
                    type: "GROUP_UPDATE",
                    group: value as ProjectGroup,
                  });
                }}
              >
                <SelectTrigger className="h-7 w-28 px-2.5">
                  {journeyCtx.state.group.charAt(0).toUpperCase() +
                    journeyCtx.state.group.slice(1)}
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="stage">Stage</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                    {/* <SelectItem value="tasker">Tasker</SelectItem> */}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
          {/* <div className="flex items-center justify-between">
              <div className="text-sm">Sort by</div>
              <Select
                value={sort.property}
                onValueChange={(value) => {
                  if (value === "lastCreatedAt") {
                    setSort({
                      property: "createdAt",
                      order: "desc",
                    });
                  } else if (value === "dueDate") {
                    setSort({
                      property: "dueDate",
                      order: "asc",
                    });
                  }
                }}
              >
                <SelectTrigger className="h-7 w-32 px-2.5">
                  {sort.property === "createdAt" ? "Last Created" : "Due Date"}
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="dueDate">Due Date</SelectItem>
                    <SelectItem value="lastCreatedAt">Last Created</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div> */}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function InactiveFilter() {
  const journeyCtx = useJourney();

  return (
    <div className="flex space-x-3">
      {/* <button
        className={clsx(
          "rounded-md border px-2 py-1 text-sm",
          journeyCtx.state.view.responsibility === "my" &&
            "border-primary-main text-primary-main",
          journeyCtx.state.view.responsibility === "all" &&
            "border-gray-300 text-gray-500 hover:bg-gray-50"
        )}
        onClick={() => {
          journeyCtx.dispatch({
            type: "VIEW_TEAM_UPDATE",
            responsibility:
              journeyCtx.state.view.responsibility === "my" ? "all" : "my",
          });
        }}
      >
        {journeyCtx.state.view.responsibility === "all"
          ? "Show My Tasks"
          : "Show All Tasks"}
      </button> */}
      <div className="space-y-1">
        <h3 className="text-xs text-gray-400">Inactive Tasks</h3>
        <div className="flex max-w-[330px]">
          <button
            className={clsx(
              "rounded-l-md border py-1 px-2.5 text-sm",
              journeyCtx.state.view.showInactive
                ? journeyCtx.state.isExternal
                  ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              !journeyCtx.state.view.showInactive && "border-r-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_SHOW_INACTIVE_UPDATE",
                showInactive: true,
              });
            }}
          >
            Show
          </button>

          <button
            className={clsx(
              "truncate rounded-r-md border py-1 px-2.5 text-sm",
              !journeyCtx.state.view.showInactive
                ? journeyCtx.state.isExternal
                  ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.showInactive && "border-l-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_SHOW_INACTIVE_UPDATE",
                showInactive: false,
              });
            }}
          >
            Hide
            {/* This is a really long customer name */}
          </button>
        </div>
      </div>
    </div>
  );
}
function CompletedFilter() {
  const journeyCtx = useJourney();

  return (
    <div className="flex space-x-3">
      {/* <button
        className={clsx(
          "rounded-md border px-2 py-1 text-sm",
          journeyCtx.state.view.responsibility === "my" &&
            "border-primary-main text-primary-main",
          journeyCtx.state.view.responsibility === "all" &&
            "border-gray-300 text-gray-500 hover:bg-gray-50"
        )}
        onClick={() => {
          journeyCtx.dispatch({
            type: "VIEW_TEAM_UPDATE",
            responsibility:
              journeyCtx.state.view.responsibility === "my" ? "all" : "my",
          });
        }}
      >
        {journeyCtx.state.view.responsibility === "all"
          ? "Show My Tasks"
          : "Show All Tasks"}
      </button> */}
      <div className="space-y-1">
        <h3 className="text-xs text-gray-400">Completed Tasks</h3>
        <div className="flex max-w-[330px]">
          <button
            className={clsx(
              "rounded-l-md border py-1 px-2.5 text-sm",
              journeyCtx.state.view.showCompleted
                ? journeyCtx.state.isExternal
                  ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              !journeyCtx.state.view.showCompleted && "border-r-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_COMPLETED_UPDATE",
                showCompleted: true,
              });
            }}
          >
            Show
          </button>

          <button
            className={clsx(
              "truncate rounded-r-md border py-1 px-2.5 text-sm",
              !journeyCtx.state.view.showCompleted
                ? journeyCtx.state.isExternal
                  ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.showCompleted && "border-l-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_COMPLETED_UPDATE",
                showCompleted: false,
              });
            }}
          >
            Hide
            {/* This is a really long customer name */}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubtasksFilter() {
  const journeyCtx = useJourney();

  return (
    <div className="flex space-x-3">
      {/* <button
        className={clsx(
          "rounded-md border px-2 py-1 text-sm",
          journeyCtx.state.view.responsibility === "my" &&
            "border-primary-main text-primary-main",
          journeyCtx.state.view.responsibility === "all" &&
            "border-gray-300 text-gray-500 hover:bg-gray-50"
        )}
        onClick={() => {
          journeyCtx.dispatch({
            type: "VIEW_TEAM_UPDATE",
            responsibility:
              journeyCtx.state.view.responsibility === "my" ? "all" : "my",
          });
        }}
      >
        {journeyCtx.state.view.responsibility === "all"
          ? "Show My Tasks"
          : "Show All Tasks"}
      </button> */}
      <div className="space-y-1">
        <h3 className="text-xs text-gray-400">Subtasks</h3>
        <div className="flex max-w-[330px]">
          <button
            className={clsx(
              "rounded-l-md border py-1 px-2.5 text-sm",
              journeyCtx.state.view.showSubtasks
                ? "border-gray-400 bg-gray-100"
                : // ? journeyCtx.state.isExternal
                  //   ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  //   : "border-primary-main bg-primary-100 text-primary-600"
                  "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              !journeyCtx.state.view.showSubtasks && "border-r-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_SHOW_SUBTASKS_UPDATE",
                showSubtasks: true,
              });
            }}
          >
            Show
          </button>

          <button
            className={clsx(
              "truncate rounded-r-md border py-1 px-2.5 text-sm",
              !journeyCtx.state.view.showSubtasks
                ? journeyCtx.state.isExternal
                  ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.showSubtasks && "border-l-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_SHOW_SUBTASKS_UPDATE",
                showSubtasks: false,
              });
            }}
          >
            Hide
            {/* This is a really long customer name */}
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskerFilter({
  instanceName,
  customerName,
}: {
  instanceName: string;
  customerName: string;
}) {
  const journeyCtx = useJourney();
  return (
    <div className="flex space-x-3">
      {/* <button
        className={clsx(
          "rounded-md border px-2 py-1 text-sm",
          journeyCtx.state.view.responsibility === "my" &&
            "border-primary-main text-primary-main",
          journeyCtx.state.view.responsibility === "all" &&
            "border-gray-300 text-gray-500 hover:bg-gray-50"
        )}
        onClick={() => {
          journeyCtx.dispatch({
            type: "VIEW_TEAM_UPDATE",
            responsibility:
              journeyCtx.state.view.responsibility === "my" ? "all" : "my",
          });
        }}
      >
        {journeyCtx.state.view.responsibility === "all"
          ? "Show My Tasks"
          : "Show All Tasks"}
      </button> */}
      <div className="space-y-1">
        <h3 className="text-xs text-gray-400">Assigned To</h3>
        <div className="flex max-w-[330px]">
          <button
            className={clsx(
              "rounded-l-md border py-1 px-2.5 text-sm",
              journeyCtx.state.view.responsibility === "all"
                ? "border-gray-400 bg-gray-100"
                : // ? journeyCtx.state.isExternal
                  //   ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  //   : "border-primary-main bg-primary-100 text-primary-600"
                  // ? "border-gray-500 bg-gray-00 text-gray-600"
                  "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.responsibility === "my" && "border-r-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_TEAM_UPDATE",
                responsibility: "all",
              });
            }}
          >
            All
          </button>
          <button
            className={clsx(
              "border-t border-b py-1 px-2.5 text-sm",
              journeyCtx.state.view.responsibility === "my"
                ? journeyCtx.state.isExternal
                  ? "border-l border-r border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-l border-r border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.responsibility === "all" && "border-r"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_TEAM_UPDATE",
                responsibility: "my",
              });
            }}
          >
            Me
          </button>
          <button
            className={clsx(
              "truncate border-t border-b py-1 px-2.5 text-sm",
              journeyCtx.state.view.responsibility === "internal"
                ? journeyCtx.state.isExternal
                  ? "border-r border-l border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-r border-l border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.responsibility === "external" && "border-l"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_TEAM_UPDATE",
                responsibility: "internal",
              });
            }}
          >
            {instanceName}
            {/* First Resonance erfergergergergegergerg */}
          </button>
          <button
            className={clsx(
              "truncate rounded-r-md border py-1 px-2.5 text-sm",
              journeyCtx.state.view.responsibility === "external"
                ? journeyCtx.state.isExternal
                  ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.responsibility === "internal" &&
                "border-l-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_TEAM_UPDATE",
                responsibility: "external",
              });
            }}
          >
            {customerName}
            {/* This is a really long customer name */}
          </button>
        </div>
      </div>
    </div>
  );
}

function TasksFilter() {
  const journeyCtx = useJourney();
  return (
    <div className="flex space-x-3">
      <div className="space-y-1">
        <h3 className="text-xs text-gray-400">Status</h3>
        <div className="flex max-w-[330px]">
          <button
            className={clsx(
              "rounded-l-md border py-1 px-2.5 text-sm",
              !journeyCtx.state.view.showOnlyActiveAndIncompleteTasks
                ? "border-gray-400 bg-gray-100"
                : // ? journeyCtx.state.isExternal
                  //   ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  //   : "border-primary-main bg-primary-100 text-primary-600"
                  "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              journeyCtx.state.view.showOnlyActiveAndIncompleteTasks &&
                "border-r-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_SHOW_ACTIVE_AND_INCOMPLETE_TASKS_UPDATE",
                showOnlyActiveAndIncompleteTasks: false,
              });
            }}
          >
            All
          </button>
          <button
            className={clsx(
              "truncate rounded-r-md border py-1 px-2.5 text-sm",
              journeyCtx.state.view.showOnlyActiveAndIncompleteTasks
                ? journeyCtx.state.isExternal
                  ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
                  : "border-primary-main bg-primary-100 text-primary-600"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              !journeyCtx.state.view.showOnlyActiveAndIncompleteTasks &&
                "border-l-0"
            )}
            onClick={() => {
              journeyCtx.dispatch({
                type: "VIEW_SHOW_ACTIVE_AND_INCOMPLETE_TASKS_UPDATE",
                showOnlyActiveAndIncompleteTasks: true,
              });
            }}
          >
            Currently Active
          </button>
        </div>
      </div>
    </div>
  );
}

// function SubtasksFilter() {
//   const journeyCtx = useJourney();
//   return (
//     <div className="flex space-x-3">
//       <div className="space-y-1">
//         <h3 className="text-xs text-gray-400">Subtasks</h3>
//         <div className="flex max-w-[330px]">
//           <button
//             className={clsx(
//               "rounded-l-md border py-1 px-2.5 text-sm",
//               journeyCtx.state.view.showSubtasks
//                 ? journeyCtx.state.isExternal
//                   ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
//                   : "border-primary-main bg-primary-100 text-primary-600"
//                 : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
//               journeyCtx.state.view.showSubtasks &&
//                 "border-r-0"
//             )}
//             onClick={() => {
//               journeyCtx.dispatch({
//                 type: "VIEW_SHOW_SUBTASKS_UPDATE",
//                 showSubtasks:true,
//               });
//             }}
//           >
//             All
//           </button>
//           <button
//             className={clsx(
//               "truncate rounded-r-md border py-1 px-2.5 text-sm",
//               !journeyCtx.state.view.showSubtasks
//                 ? journeyCtx.state.isExternal
//                   ? "border-embedPrimary bg-embedPrimary/10 text-embedPrimary"
//                   : "border-primary-main bg-primary-100 text-primary-600"
//                 : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
//               journeyCtx.state.view.showSubtasks &&
//                 "border-l-0"
//             )}
//             onClick={() => {
//               journeyCtx.dispatch({
//                 type: "VIEW_SHOW_SUBTASKS_UPDATE",
//                 showSubtasks: false,
//               });
//             }}
//           >
//             Hide
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

function GanttTestView({
  stages,
  journeyId,
  statusGroup,
  tasks,
}: {
  stages: JourneyStageObj[];
  tasks: TaskData[];
  journeyId: string;
  statusGroup: StatusGroupData;
}) {
  const [taskListNode, setTaskListNode] = useState<HTMLDivElement | null>(null);
  const [timelineHeaderNode, setTimelineHeaderNode] =
    useState<HTMLDivElement | null>(null);
  const [ganttAreaNode, setGanttAreaNode] = useState<HTMLDivElement | null>(
    null
  );

  const [dayWidth, setDayWidth] = useState(4);

  const [visibleDateRange, setVisibleDateRange] = useState({
    startDate: dayjs().subtract(8, "month").toDate(), // Start 12 months before current date
    endDate: dayjs().add(8, "month").toDate(), // End 12 months after current date
    buffer: 6, // Number of months of buffer on each side
  });

  const [timeInterval, setTimeInterval] = useState<"day" | "week" | "month">(
    "month"
  );

  // Generate time columns based on visible date range
  const timeColumns: {
    key: string;
    label: string;
    width: number;
    date: Date;
  }[] = useMemo(() => {
    const columns = [];
    const { startDate, endDate } = visibleDateRange;
    let currentDate = new Date(startDate);

    // Generate columns for each month in the range
    while (currentDate <= endDate) {
      if (timeInterval === "month") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const monthKey = `${year}-${month + 1}`;

        columns.push({
          key: monthKey,
          label: currentDate.toLocaleDateString("en-US", {
            month: "short",
          }),
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = getDateOfFirstDayOfNextMonth(currentDate);
      } else if (timeInterval === "week") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const week = currentDate.getDate() / 7;
        const weekKey = `${year}-${month + 1}-${week}`;

        columns.push({
          key: weekKey,
          label: `${dayjs(currentDate).format("MMM DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "week").toDate();
      } else if (timeInterval === "day") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const day = currentDate.getDate();
        const dayKey = `${year}-${month + 1}-${day}`;

        columns.push({
          key: dayKey,
          label: `${dayjs(currentDate).format("DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "day").toDate();
      }
    }
    return columns;
  }, [visibleDateRange, timeInterval]);
  // Generate time columns based on visible date range

  const timeUpperColumns: {
    key: string;
    label: string;
    width: number;
    date: Date;
  }[] = useMemo(() => {
    const columns = [];
    const { startDate, endDate } = visibleDateRange;
    let currentDate = new Date(startDate);

    // Generate columns for each month in the range
    while (currentDate <= endDate) {
      if (timeInterval === "month") {
        const { quarter, year, days } = getQuarterDetails(currentDate, endDate);
        // console.log(`${quarter}Q ${year}`);
        // console.log(days);
        columns.push({
          key: `${quarter}-${year}`,
          label: `${quarter}Q ${year}`,
          width: days * dayWidth,
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(days, "day").toDate();
      } else if (timeInterval === "week") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const week = currentDate.getDate() / 7;
        const weekKey = `${year}-${month + 1}-${week}`;

        columns.push({
          key: weekKey,
          label: `${dayjs(currentDate).format("MMM DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "week").toDate();
      } else if (timeInterval === "day") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const day = currentDate.getDate();
        const dayKey = `${year}-${month + 1}-${day}`;

        columns.push({
          key: dayKey,
          label: `${dayjs(currentDate).format("DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "day").toDate();
      }
    }
    return columns;
  }, [visibleDateRange, timeInterval]);

  // console.log(timeUpperColumns);

  // Calculate total width of timeline
  const totalTimelineWidth = useMemo(() => {
    return (
      (dayjs(visibleDateRange.endDate).diff(
        dayjs(visibleDateRange.startDate),
        "day"
      ) +
        1) *
      dayWidth
    );
  }, [timeColumns, timeInterval]);

  // Flag to prevent scroll loops
  const isScrollingSyncRef = useRef(false);

  // State to track if we're currently extending the timeline
  const [isExtendingTimeline, setIsExtendingTimeline] = useState(false);

  // A ref to store the rAF ID so we only do sync once per frame
  const syncRafId = useRef<number>(0);

  // Scroll sync logic
  useLayoutEffect(() => {
    const ganttArea = ganttAreaNode;
    const timelineHeader = timelineHeaderNode;
    const taskList = taskListNode;

    console.log(ganttArea);
    console.log(timelineHeader);
    console.log(taskList);

    if (!ganttArea || !timelineHeader || !taskList) return;

    // Cancel any pending frames on cleanup
    let isUnmounting = false;
    const cancelRaf = () => {
      if (syncRafId.current) {
        cancelAnimationFrame(syncRafId.current);
        syncRafId.current = 0;
      }
    };

    const syncScrollPositions = (scrollSource: "gantt" | "header" | "list") => {
      if (isUnmounting) return;
      if (!ganttArea || !timelineHeader || !taskList) {
        return;
      }

      // Whichever element triggered the scroll, read its scrollLeft/scrollTop
      // and set the others accordingly.
      if (scrollSource === "gantt") {
        timelineHeader.scrollLeft = ganttArea.scrollLeft;
        taskList.scrollTop = ganttArea.scrollTop;
        // handleInfiniteScroll(ganttArea.scrollLeft);
      } else if (scrollSource === "header") {
        ganttArea.scrollLeft = timelineHeader.scrollLeft;
        // handleInfiniteScroll(timelineHeader.scrollLeft);
      } else if (scrollSource === "list") {
        ganttArea.scrollTop = taskList.scrollTop;
      }
    };

    const scheduleSync = (scrollSource: "gantt" | "header" | "list") => {
      if (!syncRafId.current) {
        // If no request is in progress, queue one
        syncRafId.current = requestAnimationFrame(() => {
          syncRafId.current = 0; // reset
          syncScrollPositions(scrollSource);
        });
      }
    };

    const handleGanttScroll = () => scheduleSync("gantt");
    const handleHeaderScroll = () => scheduleSync("header");
    const handleListScroll = () => scheduleSync("list");

    // Attach listeners
    ganttArea.addEventListener("scroll", handleGanttScroll, { passive: true });
    timelineHeader.addEventListener("scroll", handleHeaderScroll, {
      passive: true,
    });
    taskList.addEventListener("scroll", handleListScroll, { passive: true });

    // Cleanup
    return () => {
      isUnmounting = true;
      cancelRaf();
      ganttArea.removeEventListener("scroll", handleGanttScroll);
      timelineHeader.removeEventListener("scroll", handleHeaderScroll);
      taskList.removeEventListener("scroll", handleListScroll);
    };
  }, [ganttAreaNode, timelineHeaderNode, taskListNode]);
  // }, [handleInfiniteScroll, ganttAreaNode, timelineHeaderNode, taskListNode]);

  const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>(
    () => {
      // build an object: { s1: true, s2: true, ... }
      const initial: Record<string, boolean> = {};
      for (const stage of stages) {
        if (!stage._id) {
          continue;
        }
        initial[stage._id.toString()] = true;
      }
      return initial;
    }
  );
  const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>(
    {}
  );

  const handleToggleStage = useCallback((stageId: string) => {
    setExpandedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  }, []);

  const handleToggleTask = useCallback((taskId: string) => {
    setExpandedTasks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  }, []);

  // Build children map once (or whenever tasks change)
  const { childrenMap, topLevelTasksByStage } = useMemo(() => {
    return buildTaskIndices(tasks);
    // eslint-disable-next-line
  }, [tasks]);

  // Flatten data based on expansions
  const flattened = useMemo(() => {
    return flattenData(
      stages,
      topLevelTasksByStage,
      childrenMap,
      expandedStages,
      expandedTasks
    );
  }, [
    stages,
    topLevelTasksByStage,
    childrenMap,
    expandedStages,
    expandedTasks,
  ]);

  return (
    <>
      <style>{hideScrollbarCSS}</style>
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200/70 px-8">
        <div>
          <Button variant={"secondary"}>Columns</Button>
        </div>
        <div>
          <Select>
            <SelectTrigger>Weeks</SelectTrigger>
            <SelectContent>
              <SelectItem value="Weeks">Weeks</SelectItem>
              <SelectItem value="Months">Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70">
        <div className="flex h-full w-96 items-center border-r border-gray-200/70 bg-gray-50 px-8 text-sm">
          Tasks
        </div>
        {/* <div className="flex h-full flex-1 items-center bg-gray-100"> */}
        <div
          ref={setTimelineHeaderNode}
          className="hide-scrollbar h-full flex-1 overflow-x-auto overflow-y-hidden"
        >
          {/* Timeline header content */}
          <div>
            <div className="flex h-full" style={{ width: totalTimelineWidth }}>
              {timeUpperColumns.map((column, index) => (
                <div
                  key={column.key}
                  className={clsx(
                    "relative flex h-6 flex-shrink-0 items-center border-b border-r border-gray-200",
                    index % 2 === 0 ? "bg-gray-100" : ""
                  )}
                  style={{ width: `${column.width}px` }}
                >
                  <span className="sticky left-0 truncate px-2 text-xs font-medium">
                    {column.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex h-full" style={{ width: totalTimelineWidth }}>
              {timeColumns.map((column) => (
                <div
                  key={column.key}
                  className={
                    "flex h-6 flex-shrink-0 items-center justify-center border-r border-gray-200"
                  }
                  style={{ width: `${column.width}px` }}
                >
                  <span className="truncate text-xs font-medium">
                    {column.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* </div> */}
      </div>
      <div className="flex min-h-0 flex-1">
        <TaskListGantt
          flattened={flattened}
          expandedStages={expandedStages}
          expandedTasks={expandedTasks}
          handleToggleStage={handleToggleStage}
          handleToggleTask={handleToggleTask}
          childrenMap={childrenMap}
          topLevelTasksByStage={topLevelTasksByStage}
          ref={(node) => {
            console.log("Parent sees the forwardedRef as:", node);
            setTaskListNode(node);
          }}
        />
        <div ref={setGanttAreaNode} className="relative flex-1 overflow-auto">
          <div
            style={{ width: totalTimelineWidth }}
            // className="bg-gradient-to-br from-yellow-500 to-blue-500"
          >
            {flattened.map((task, index) => {
              return (
                <div className="h-10 w-full border-b border-gray-200/70" />
              );
            })}
            {/* Vertical grid lines for months */}
          </div>
        </div>
      </div>
      {/* {stages
        .filter((s) => {
          if (journeyCtx.state.isExternal) {
            return (
              !s.tasks?.filter((t) => !t.isVisible).length ||
              s.tasks.filter((t) => t.isVisible).length > 0
            );
          }
          return true;
        })
        .map((stage, index) => (
          <GanttTaskList
            tasks={tasks.filter(
              (t) => t.journeyStage?._id === stage._id?.toString()
            )}
            title={stage.name}
            index={index}
          />
        ))} */}
      {/* {stages
        .filter((s) => {
          if (journeyCtx.state.isExternal) {
            return (
              !s.tasks?.filter((t) => !t.isVisible).length ||
              s.tasks.filter((t) => t.isVisible).length > 0
            );
          }
          return true;
        })
        .map((stage) => (
          <>
            {stage._id && (
              <StageTaskList
                key={stage._id?.toString() ?? Math.random()}
                stage={stage}
                stageId={stage._id.toString()}
              />
            )}
          </>
        ))} */}
    </>
  );
}

// Build the child maps
function buildTaskIndices(tasks: TaskData[]) {
  const childrenMap: Record<string, TaskData[]> = {};
  const topLevelTasksByStage: Record<string, TaskData[]> = {};

  for (const t of tasks) {
    const _id = t._id;
    const stageId = t.journeyStage?._id;
    const parentTaskId = t.parentTask?._id;
    if (!_id) {
      continue;
    }
    // Ensure each key is initialized
    if (!childrenMap[_id]) {
      childrenMap[_id] = [];
    }
    if (parentTaskId) {
      if (!childrenMap[parentTaskId]) {
        childrenMap[parentTaskId] = [];
      }
      childrenMap[parentTaskId].push(t);
    } else if (stageId) {
      if (!topLevelTasksByStage[stageId]) {
        topLevelTasksByStage[stageId] = [];
      }
      topLevelTasksByStage[stageId].push(t);
    }
  }

  return { childrenMap, topLevelTasksByStage };
}

type FlatRow =
  | {
      kind: "stage";
      level: number;
      stage: JourneyStageObj;
    }
  | {
      kind: "task";
      task: TaskData;
      level: number;
    };

function flattenData(
  stages: JourneyStageObj[],
  topLevelTasksByStage: Record<string, TaskData[]>,
  childrenMap: Record<string, TaskData[]>,
  expandedStages: Record<string, boolean>,
  expandedTasks: Record<string, boolean>
): FlatRow[] {
  const flatRows: FlatRow[] = [];

  function flattenTaskRecursive(task: TaskData, level: number) {
    flatRows.push({ kind: "task", task, level });

    if (!task._id) {
      return;
    }
    if (expandedTasks[task._id]) {
      const children = childrenMap[task._id] || [];
      for (const child of children) {
        flattenTaskRecursive(child, level + 1);
      }
    }
  }

  for (const stage of stages) {
    if (!stage._id) {
      continue;
    }
    // always level 0 for a stage
    flatRows.push({ kind: "stage", stage, level: 0 });

    if (expandedStages[stage._id.toString()]) {
      const topLevelTasks = topLevelTasksByStage[stage._id.toString()] || [];
      for (const t of topLevelTasks) {
        flattenTaskRecursive(t, 1);
      }
    }
  }

  return flatRows;
}

// const DAY_VIEW_DAY_WIDTH = 40;
// const WEEK_VIEW_DAY_WIDTH = 20;
// const MONTH_VIEW_DAY_WIDTH = 4;

function getWidth(
  date: Date,
  timeInterval: "day" | "week" | "month",
  endDate: Date,
  dayWidth: number
) {
  if (timeInterval === "month") {
    let daysInMonth = dayjs(date).daysInMonth();
    if (dayjs(date).add(daysInMonth, "day").isAfter(endDate)) {
      daysInMonth = dayjs(endDate).diff(date, "day") + 1;
    } else {
      daysInMonth = daysInMonth - date.getDate() + 1;
    }
    return daysInMonth * dayWidth;
  } else if (timeInterval === "week") {
    const daysInWeek = 7;
    return daysInWeek * dayWidth;
  } else {
    return dayWidth;
  }
}

export function daysInQuarterFrom(date: Date, maxDate: Date): number {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based: Jan=0, Feb=1, Mar=2, ...
  let lastMonthOfQuarter: number;
  let lastDayOfQuarter: number;

  if (month < 3) {
    // Q1: Jan-Mar
    lastMonthOfQuarter = 2; // March
    lastDayOfQuarter = 31;
  } else if (month < 6) {
    // Q2: Apr-Jun
    lastMonthOfQuarter = 5; // June
    lastDayOfQuarter = 30;
  } else if (month < 9) {
    // Q3: Jul-Sep
    lastMonthOfQuarter = 8; // September
    lastDayOfQuarter = 30;
  } else {
    // Q4: Oct-Dec
    lastMonthOfQuarter = 11; // December
    lastDayOfQuarter = 31;
  }

  // Create "normalized" dates at midnight so time doesn’t affect the calculation
  const startOfGivenDate = new Date(year, month, date.getDate());
  let endOfQuarter = new Date(year, lastMonthOfQuarter, lastDayOfQuarter);

  if (endOfQuarter > maxDate) {
    endOfQuarter = maxDate;
  }

  return dayjs(endOfQuarter).diff(startOfGivenDate, "day") + 1;
}

function getQuarterDetails(
  date: Date,
  maxDate: Date
): {
  quarter: number;
  year: number;
  days: number;
} {
  const month = date.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  const year = dayjs(date).year();
  const daysInQuarter = daysInQuarterFrom(date, maxDate);
  return { quarter, year, days: daysInQuarter };
}

function getDateOfFirstDayOfNextMonth(date: Date): Date {
  let year = date.getFullYear();
  let month = date.getMonth();

  // If we’re in December (month = 11), move to January (0) and increment the year
  if (month === 11) {
    year++;
    month = 0;
  } else {
    month++;
  }

  // First day of the calculated month
  return new Date(year, month, 1);
}

// CSS to hide scrollbars
const hideScrollbarCSS = `
/* Hide scrollbar for Chrome, Safari and Opera */
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.hide-scrollbar {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
`;

function getDaysFromRelativeDueDate(
  relativeDueDate: TaskData["relativeDueDate"]
): number {
  if (!relativeDueDate) {
    return 0;
  }
  if (relativeDueDate.unit === "hour") {
    return 1;
  } else if (relativeDueDate.unit === "day") {
    return relativeDueDate.value;
  } else if (relativeDueDate.unit === "week") {
    return relativeDueDate.value * 7;
  }
  return 0;
}

function getTaskBarDetails(
  task: TaskData,
  dayWidth: number,
  visibleStart: Date,
  settings: InstanceWorkingTimeSettings
): {
  position: {
    left: number;
  };
  width: number;
} {
  const days = getDaysFromRelativeDueDate(task.relativeDueDate);

  if (task.dueDate) {
    const dueDate = new Date(task.dueDate);
    const startDate = dayjs(dueDate).subtract(days, "day");
    const left = startDate.diff(visibleStart, "day") * dayWidth;
    const width = days * dayWidth;
    return { position: { left }, width };
  } else if (task.durations?.forecasted) {
    const dueDate = dayjs(
      calculateEndDate(
        new Date(),
        task.durations.forecasted,
        settings.workingTimeDetails,
        settings.timezone
      )
    ).format("MMM DD YYYY");
    const startDate = dayjs(dueDate).subtract(days, "day");
    console.log(task.title, days, dueDate, startDate.format("MMM DD YYYY"));
    const left = startDate.diff(visibleStart, "day") * dayWidth;
    const width = days * dayWidth;
    return { position: { left }, width };
  } else {
    return { position: { left: 0 }, width: 0 };
  }
}

function GanttView({
  stages,
  journeyId,
  statusGroup,
  tasks,
}: {
  stages: JourneyStageObj[];
  tasks: TaskData[];
  journeyId: string;
  statusGroup: StatusGroupData;
}) {
  const [taskListNode, setTaskListNode] = useState<HTMLDivElement | null>(null);
  const [timelineHeaderNode, setTimelineHeaderNode] =
    useState<HTMLDivElement | null>(null);
  const [ganttAreaNode, setGanttAreaNode] = useState<HTMLDivElement | null>(
    null
  );
  const { phaseId } = useParams();
  const { data: settings } = useGetInstanceWorkingTimeSettings(String(phaseId));

  const [dayWidth, setDayWidth] = useState(12);

  const [visibleDateRange, setVisibleDateRange] = useState({
    startDate: dayjs().subtract(1, "month").toDate(), // Start 12 months before current date
    endDate: dayjs().add(8, "month").toDate(), // End 12 months after current date
    buffer: 6, // Number of months of buffer on each side
  });

  const [timeInterval, setTimeInterval] = useState<"day" | "week" | "month">(
    "month"
  );

  // Generate time columns based on visible date range
  const timeColumns: {
    key: string;
    label: string;
    width: number;
    date: Date;
  }[] = useMemo(() => {
    const columns = [];
    const { startDate, endDate } = visibleDateRange;
    let currentDate = new Date(startDate);

    // Generate columns for each month in the range
    while (currentDate <= endDate) {
      if (timeInterval === "month") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const monthKey = `${year}-${month + 1}`;

        columns.push({
          key: monthKey,
          label: currentDate.toLocaleDateString("en-US", {
            month: "short",
          }),
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = getDateOfFirstDayOfNextMonth(currentDate);
      } else if (timeInterval === "week") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const week = currentDate.getDate() / 7;
        const weekKey = `${year}-${month + 1}-${week}`;

        columns.push({
          key: weekKey,
          label: `${dayjs(currentDate).format("MMM DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "week").toDate();
      } else if (timeInterval === "day") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const day = currentDate.getDate();
        const dayKey = `${year}-${month + 1}-${day}`;

        columns.push({
          key: dayKey,
          label: `${dayjs(currentDate).format("DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "day").toDate();
      }
    }
    return columns;
  }, [visibleDateRange, timeInterval]);
  // Generate time columns based on visible date range

  const timeUpperColumns: {
    key: string;
    label: string;
    width: number;
    date: Date;
  }[] = useMemo(() => {
    const columns = [];
    const { startDate, endDate } = visibleDateRange;
    let currentDate = new Date(startDate);

    // Generate columns for each month in the range
    while (currentDate <= endDate) {
      if (timeInterval === "month") {
        const { quarter, year, days } = getQuarterDetails(currentDate, endDate);
        // console.log(`${quarter}Q ${year}`);
        // console.log(days);
        columns.push({
          key: `${quarter}-${year}`,
          label: `${quarter}Q ${year}`,
          width: days * dayWidth,
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(days, "day").toDate();
      } else if (timeInterval === "week") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const week = currentDate.getDate() / 7;
        const weekKey = `${year}-${month + 1}-${week}`;

        columns.push({
          key: weekKey,
          label: `${dayjs(currentDate).format("MMM DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "week").toDate();
      } else if (timeInterval === "day") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const day = currentDate.getDate();
        const dayKey = `${year}-${month + 1}-${day}`;

        columns.push({
          key: dayKey,
          label: `${dayjs(currentDate).format("DD")}`,
          width: getWidth(currentDate, timeInterval, endDate, dayWidth),
          date: new Date(currentDate),
        });
        currentDate = dayjs(currentDate).add(1, "day").toDate();
      }
    }
    return columns;
  }, [visibleDateRange, timeInterval]);

  // console.log(timeUpperColumns);

  // Calculate total width of timeline
  const totalTimelineWidth = useMemo(() => {
    return (
      (dayjs(visibleDateRange.endDate).diff(
        dayjs(visibleDateRange.startDate),
        "day"
      ) +
        1) *
      dayWidth
    );
  }, [timeColumns, timeInterval]);

  // Flag to prevent scroll loops
  const isScrollingSyncRef = useRef(false);

  // State to track if we're currently extending the timeline
  const [isExtendingTimeline, setIsExtendingTimeline] = useState(false);

  // // Handle horizontal scrolling to implement "infinite" scrolling
  // const handleInfiniteScroll = (scrollLeft: number) => {
  //   // Don't process if we're already extending the timeline
  //   if (isExtendingTimeline) return;

  //   const ganttArea = ganttAreaRef.current;
  //   if (!ganttArea) return;

  //   // Calculate buffer width in pixels
  //   const bufferWidth = visibleDateRange.buffer * dayWidth;
  //   const viewportWidth = ganttArea.clientWidth;

  //   // Check if we're approaching the left edge (need to extend into the past)
  //   if (scrollLeft < bufferWidth) {
  //     setIsExtendingTimeline(true);

  //     // Add more months to the beginning of the timeline
  //     const newStartDate = new Date(visibleDateRange.startDate);
  //     newStartDate.setMonth(newStartDate.getMonth() - 6); // Add 6 more months to the past

  //     // Remember current scroll position relative to the content
  //     const scrollPos = scrollLeft;
  //     const oldTimelineWidth = totalTimelineWidth;

  //     setVisibleDateRange((prev) => ({
  //       ...prev,
  //       startDate: newStartDate,
  //     }));

  //     // After the timeline is extended, restore scroll position
  //     setTimeout(() => {
  //       // Calculate new scroll position to maintain the same view
  //       // The scroll position needs to account for the added width
  //       const widthAdded = 6 * dayWidth; // 6 months added
  //       if (ganttAreaRef.current) {
  //         ganttAreaRef.current.scrollLeft = scrollPos + widthAdded;
  //       }

  //       setIsExtendingTimeline(false);
  //     }, 10);
  //   }

  //   // Check if we're approaching the right edge (need to extend into the future)
  //   if (scrollLeft > totalTimelineWidth - viewportWidth - bufferWidth) {
  //     setIsExtendingTimeline(true);

  //     // Add more months to the end of the timeline
  //     const newEndDate = new Date(visibleDateRange.endDate);
  //     newEndDate.setMonth(newEndDate.getMonth() + 6); // Add 6 more months to the future

  //     setVisibleDateRange((prev) => ({
  //       ...prev,
  //       endDate: newEndDate,
  //     }));

  //     // No need to adjust scroll position when extending into the future
  //     setTimeout(() => {
  //       setIsExtendingTimeline(false);
  //     }, 10);
  //   }
  // };

  // // Optimize scroll synchronization
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     const ganttArea = ganttAreaRef.current;
  //     const timelineHeader = timelineHeaderRef.current;
  //     const taskList = taskListRef.current;
  //     console.log(ganttArea);
  //     console.log(timelineHeader);
  //     console.log(taskList);

  //     if (!ganttArea || !timelineHeader || !taskList) return;

  //     console.log("works");
  //     // Direct and simple scroll synchronization
  //     const handleGanttScroll = () => {
  //       if (!isScrollingSyncRef.current) {
  //         isScrollingSyncRef.current = true;

  //         // Sync horizontal scroll to timeline header
  //         timelineHeader.scrollLeft = ganttArea.scrollLeft;
  //         // Sync vertical scroll to task list
  //         taskList.scrollTop = ganttArea.scrollTop;

  //         // Check if we need to extend the timeline
  //         handleInfiniteScroll(ganttArea.scrollLeft);

  //         isScrollingSyncRef.current = false;
  //       }
  //     };

  //     const handleTimelineScroll = () => {
  //       if (!isScrollingSyncRef.current) {
  //         isScrollingSyncRef.current = true;

  //         // Sync horizontal scroll to gantt area
  //         ganttArea.scrollLeft = timelineHeader.scrollLeft;

  //         // Check if we need to extend the timeline
  //         handleInfiniteScroll(timelineHeader.scrollLeft);

  //         isScrollingSyncRef.current = false;
  //       }
  //     };

  //     const handleTaskListScroll = () => {
  //       if (!isScrollingSyncRef.current) {
  //         isScrollingSyncRef.current = true;

  //         // Sync vertical scroll to gantt area
  //         ganttArea.scrollTop = taskList.scrollTop;

  //         isScrollingSyncRef.current = false;
  //       }
  //     };

  //     // Add optimized event listeners
  //     ganttArea.addEventListener("scroll", handleGanttScroll, {
  //       passive: true,
  //     });
  //     timelineHeader.addEventListener("scroll", handleTimelineScroll, {
  //       passive: true,
  //     });
  //     taskList.addEventListener("scroll", handleTaskListScroll, {
  //       passive: true,
  //     });

  //     return () => {
  //       ganttArea.removeEventListener("scroll", handleGanttScroll);
  //       timelineHeader.removeEventListener("scroll", handleTimelineScroll);
  //       taskList.removeEventListener("scroll", handleTaskListScroll);
  //     };
  //   }, 100); // Small delay

  //   return () => clearTimeout(timer);
  // }, [handleInfiniteScroll, totalTimelineWidth]);

  // This function extends the timeline only when user is near the edges.
  // const handleInfiniteScroll = useCallback(
  //   (scrollLeft: number) => {
  //     if (isExtendingTimeline) return;

  //     const ganttArea = ganttAreaNode;
  //     if (!ganttArea) return;

  //     const bufferWidth = visibleDateRange.buffer * dayWidth;
  //     const viewportWidth = ganttArea.clientWidth;

  //     // Extend timeline to the left
  //     if (scrollLeft < bufferWidth) {
  //       setIsExtendingTimeline(true);

  //       const newStartDate = new Date(visibleDateRange.startDate);
  //       newStartDate.setMonth(newStartDate.getMonth() - 6); // Add 6 more months to the past
  //       const oldScrollPos = scrollLeft;

  //       setVisibleDateRange((prev) => ({
  //         ...prev,
  //         startDate: newStartDate,
  //       }));

  //       // After extending, restore scroll so the user sees the same position
  //       // that they had on screen before.
  //       requestAnimationFrame(() => {
  //         const widthAdded = 6 * dayWidth * 30;
  //         // ^ If you're calculating exact days for each “month”, you may want
  //         //   to do a more precise approach. For a rough approach, 6 months ≈ 6*30 days
  //         //   times dayWidth.

  //         if (ganttAreaNode) {
  //           ganttAreaNode.scrollLeft = oldScrollPos + widthAdded;
  //         }
  //         setIsExtendingTimeline(false);
  //       });
  //     }

  //     // Extend timeline to the right
  //     if (scrollLeft > totalTimelineWidth - viewportWidth - bufferWidth) {
  //       setIsExtendingTimeline(true);

  //       const newEndDate = new Date(visibleDateRange.endDate);
  //       newEndDate.setMonth(newEndDate.getMonth() + 6); // +6 months

  //       setVisibleDateRange((prev) => ({
  //         ...prev,
  //         endDate: newEndDate,
  //       }));

  //       requestAnimationFrame(() => {
  //         setIsExtendingTimeline(false);
  //       });
  //     }
  //   },
  //   [
  //     isExtendingTimeline,
  //     visibleDateRange,
  //     totalTimelineWidth,
  //     dayWidth,
  //     setVisibleDateRange,
  //   ]
  // );

  // A ref to store the rAF ID so we only do sync once per frame
  const syncRafId = useRef<number>(0);

  // Scroll sync logic
  useLayoutEffect(() => {
    const ganttArea = ganttAreaNode;
    const timelineHeader = timelineHeaderNode;
    const taskList = taskListNode;

    console.log(ganttArea);
    console.log(timelineHeader);
    console.log(taskList);

    if (!ganttArea || !timelineHeader || !taskList) return;

    // Cancel any pending frames on cleanup
    let isUnmounting = false;
    const cancelRaf = () => {
      if (syncRafId.current) {
        cancelAnimationFrame(syncRafId.current);
        syncRafId.current = 0;
      }
    };

    const syncScrollPositions = (scrollSource: "gantt" | "header" | "list") => {
      if (isUnmounting) return;
      if (!ganttArea || !timelineHeader || !taskList) {
        return;
      }

      // Whichever element triggered the scroll, read its scrollLeft/scrollTop
      // and set the others accordingly.
      if (scrollSource === "gantt") {
        timelineHeader.scrollLeft = ganttArea.scrollLeft;
        taskList.scrollTop = ganttArea.scrollTop;
        // handleInfiniteScroll(ganttArea.scrollLeft);
      } else if (scrollSource === "header") {
        ganttArea.scrollLeft = timelineHeader.scrollLeft;
        // handleInfiniteScroll(timelineHeader.scrollLeft);
      } else if (scrollSource === "list") {
        ganttArea.scrollTop = taskList.scrollTop;
      }
    };

    const scheduleSync = (scrollSource: "gantt" | "header" | "list") => {
      if (!syncRafId.current) {
        // If no request is in progress, queue one
        syncRafId.current = requestAnimationFrame(() => {
          syncRafId.current = 0; // reset
          syncScrollPositions(scrollSource);
        });
      }
    };

    const handleGanttScroll = () => scheduleSync("gantt");
    const handleHeaderScroll = () => scheduleSync("header");
    const handleListScroll = () => scheduleSync("list");

    // Attach listeners
    ganttArea.addEventListener("scroll", handleGanttScroll, { passive: true });
    timelineHeader.addEventListener("scroll", handleHeaderScroll, {
      passive: true,
    });
    taskList.addEventListener("scroll", handleListScroll, { passive: true });

    // Cleanup
    return () => {
      isUnmounting = true;
      cancelRaf();
      ganttArea.removeEventListener("scroll", handleGanttScroll);
      timelineHeader.removeEventListener("scroll", handleHeaderScroll);
      taskList.removeEventListener("scroll", handleListScroll);
    };
  }, [ganttAreaNode, timelineHeaderNode, taskListNode]);
  // }, [handleInfiniteScroll, ganttAreaNode, timelineHeaderNode, taskListNode]);

  const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>(
    () => {
      // build an object: { s1: true, s2: true, ... }
      const initial: Record<string, boolean> = {};
      for (const stage of stages) {
        if (!stage._id) {
          continue;
        }
        initial[stage._id.toString()] = true;
      }
      return initial;
    }
  );
  const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>(
    {}
  );

  const handleToggleStage = useCallback((stageId: string) => {
    setExpandedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  }, []);

  const handleToggleTask = useCallback((taskId: string) => {
    setExpandedTasks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  }, []);

  // Build children map once (or whenever tasks change)
  const { childrenMap, topLevelTasksByStage } = useMemo(() => {
    return buildTaskIndices(tasks);
    // eslint-disable-next-line
  }, [tasks]);

  // Flatten data based on expansions
  const flattened = useMemo(() => {
    return flattenData(
      stages,
      topLevelTasksByStage,
      childrenMap,
      expandedStages,
      expandedTasks
    );
  }, [
    stages,
    topLevelTasksByStage,
    childrenMap,
    expandedStages,
    expandedTasks,
  ]);

  // useEffect(() => {
  //   const container = ganttAreaNode;
  //   if (container) {
  //     container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
  //   }
  // }, []);

  if (!settings) {
    return null;
  }
  return (
    <>
      <style>{hideScrollbarCSS}</style>
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200/70 px-8">
        <div>
          <Button variant={"secondary"}>Columns</Button>
        </div>
        <div>
          <Select>
            <SelectTrigger>Weeks</SelectTrigger>
            <SelectContent>
              <SelectItem value="Weeks">Weeks</SelectItem>
              <SelectItem value="Months">Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70">
        <div className="flex h-full w-96 items-center border-r border-gray-200/70 bg-gray-50 px-8 text-sm">
          Tasks
        </div>
        {/* <div className="flex h-full flex-1 items-center bg-gray-100"> */}
        <div
          ref={setTimelineHeaderNode}
          className="hide-scrollbar h-full flex-1 overflow-x-auto overflow-y-hidden"
        >
          {/* Timeline header content */}
          <div>
            <div className="flex h-full" style={{ width: totalTimelineWidth }}>
              {timeUpperColumns.map((column, index) => (
                <div
                  key={column.key}
                  className={clsx(
                    "relative flex h-6 flex-shrink-0 items-center border-b border-r border-gray-200",
                    index % 2 === 0 ? "bg-gray-100" : ""
                  )}
                  style={{ width: `${column.width}px` }}
                >
                  <span className="sticky left-0 truncate px-2 text-xs font-medium">
                    {column.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex h-full" style={{ width: totalTimelineWidth }}>
              {timeColumns.map((column) => (
                <div
                  key={column.key}
                  className={
                    "flex h-6 flex-shrink-0 items-center justify-center border-r border-gray-200"
                  }
                  style={{ width: `${column.width}px` }}
                >
                  <span className="truncate text-xs font-medium">
                    {column.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* </div> */}
      </div>
      <div className="flex min-h-0 flex-1">
        <TaskListGantt
          flattened={flattened}
          expandedStages={expandedStages}
          expandedTasks={expandedTasks}
          handleToggleStage={handleToggleStage}
          handleToggleTask={handleToggleTask}
          childrenMap={childrenMap}
          topLevelTasksByStage={topLevelTasksByStage}
          ref={(node) => {
            console.log("Parent sees the forwardedRef as:", node);
            setTaskListNode(node);
          }}
        />
        <div ref={setGanttAreaNode} className="relative flex-1 overflow-auto">
          <div
            style={{ width: totalTimelineWidth }}
            // className="bg-gradient-to-br from-yellow-500 to-blue-500"
          >
            {flattened.map((row, index) => {
              if (row.kind === "task") {
                const { position, width } = getTaskBarDetails(
                  row.task,
                  dayWidth,
                  visibleDateRange.startDate,
                  settings
                );

                return (
                  <div className="relative h-10 w-full border-b border-gray-200/70">
                    <div
                      className="absolute top-2.5 h-5 rounded bg-blue-200"
                      style={{
                        width: `${width}px`,
                        left: `${position.left}px`,
                      }}
                    />
                  </div>
                );
              } else {
                return (
                  <div className="h-10 w-full border-b border-gray-200/70" />
                );
              }
            })}
            {/* Vertical grid lines for months */}
          </div>
        </div>
      </div>
      {/* {stages
        .filter((s) => {
          if (journeyCtx.state.isExternal) {
            return (
              !s.tasks?.filter((t) => !t.isVisible).length ||
              s.tasks.filter((t) => t.isVisible).length > 0
            );
          }
          return true;
        })
        .map((stage, index) => (
          <GanttTaskList
            tasks={tasks.filter(
              (t) => t.journeyStage?._id === stage._id?.toString()
            )}
            title={stage.name}
            index={index}
          />
        ))} */}
      {/* {stages
        .filter((s) => {
          if (journeyCtx.state.isExternal) {
            return (
              !s.tasks?.filter((t) => !t.isVisible).length ||
              s.tasks.filter((t) => t.isVisible).length > 0
            );
          }
          return true;
        })
        .map((stage) => (
          <>
            {stage._id && (
              <StageTaskList
                key={stage._id?.toString() ?? Math.random()}
                stage={stage}
                stageId={stage._id.toString()}
              />
            )}
          </>
        ))} */}
    </>
  );
}
type TaskListGanttProps = {
  flattened: FlatRow[];
  expandedStages: Record<string, boolean>;
  expandedTasks: Record<string, boolean>;
  handleToggleStage: (stageId: string) => void;
  handleToggleTask: (taskId: string) => void;
  childrenMap: Record<string, TaskData[]>;
  topLevelTasksByStage: Record<string, TaskData[]>;
};

// forwardRef is declared with the ref type as the first generic parameter,
// and your props type as the second generic parameter
const TaskListGantt = forwardRef<HTMLDivElement, TaskListGanttProps>(
  (
    {
      flattened,
      expandedStages,
      expandedTasks,
      handleToggleStage,
      handleToggleTask,
      childrenMap,
      topLevelTasksByStage,
    },
    ref // <-- forwardRef supplies "ref" here as the second argument
  ) => {
    // Child’s own local ref:
    const localRef = useRef<HTMLDivElement>(null);

    // Forward that DOM node to parent’s ref:
    // This exposes localRef.current to the parent
    useImperativeHandle(ref, () => localRef.current as HTMLDivElement, []);

    // react-window row renderer
    const Row = ({ index, style }: ListChildComponentProps) => {
      const row = flattened[index];

      if (row.kind === "stage" && row.stage) {
        if (!!row.stage._id) {
          const stageId = row.stage._id.toString();
          const isExpanded = !!expandedStages[stageId];
          return (
            <div
              style={{ ...style }}
              className={clsx(
                "flex h-10 cursor-pointer items-center border-b border-gray-200/70 pl-5 pr-3 hover:bg-gray-50"
                // index === 0 && "border-t"
              )}
            >
              <div
                className="group flex h-10 w-8 shrink-0 items-center justify-center"
                onClick={() => handleToggleStage(stageId)}
              >
                <div className="cursor-pointer rounded-md p-[3px] group-hover:bg-gray-200/70">
                  <TriangleRightIcon
                    className={clsx(
                      "h-4 w-4 text-gray-300 group-hover:text-gray-400",
                      isExpanded && "rotate-90"
                    )}
                  />
                </div>
              </div>
              <div className="min-w-0 truncate text-sm font-medium">
                {row.stage.name}
              </div>
            </div>
          );
        } else {
          return null;
        }
      } else if (row.kind === "task" && row.task) {
        if (!!row.task._id) {
          const taskId = row.task._id;
          const isExpanded = !!expandedTasks[taskId];
          const hasChildren = childrenMap[taskId]?.length > 0;
          return (
            <div
              style={{ ...style, paddingLeft: row.level * 24 + 5 * 4 }}
              className="flex h-10 cursor-pointer items-center border-b border-gray-200/70 pr-3 hover:bg-gray-50"
            >
              {hasChildren ? (
                <div
                  className="group flex h-10 w-8 items-center justify-center"
                  onClick={() => handleToggleTask(taskId)}
                >
                  <div className="cursor-pointer rounded-md p-[3px] group-hover:bg-gray-200/70">
                    <TriangleRightIcon
                      className={clsx(
                        "h-4 w-4 text-gray-300 group-hover:text-gray-400",
                        isExpanded && "rotate-90"
                      )}
                    />
                  </div>
                </div>
              ) : (
                <div className="w-8 shrink-0" />
              )}
              <span
                // style={{ marginLeft: 8 }}
                className="truncate text-sm text-gray-700"
              >
                {row.task.title}
              </span>
            </div>
          );
        } else {
          return null;
        }
      }

      // fallback (should not happen unless data is malformed)
      return <div style={style}>Unknown row</div>;
    };

    return (
      <div className="min-h-0 w-96 border-r border-gray-200/70" ref={localRef}>
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <List
              className="hide-scrollbar"
              outerRef={localRef}
              height={height}
              width={width}
              itemCount={flattened.length}
              itemSize={10 * 4}
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      </div>
    );
  }
);

function ListView({
  stages,
  journeyId,
  statusGroup,
  tasks,
}: {
  stages: JourneyStageObj[];
  tasks: TaskWithChildren[];
  journeyId: string;
  statusGroup: StatusGroupData;
}) {
  const journeyCtx = useJourney();
  return (
    <div className="overflow-auto pb-6">
      {stages
        .filter((s) => {
          if (journeyCtx.state.isExternal) {
            return (
              !s.tasks?.filter((t) => !t.isVisible).length ||
              s.tasks.filter((t) => t.isVisible).length > 0
            );
          }
          return true;
        })
        .map((stage, index) => (
          <TaskList
            tasks={tasks.filter(
              (t) => t.journeyStage?._id === stage._id?.toString()
            )}
            title={stage.name}
            index={index}
          />
        ))}
      {/* {stages
        .filter((s) => {
          if (journeyCtx.state.isExternal) {
            return (
              !s.tasks?.filter((t) => !t.isVisible).length ||
              s.tasks.filter((t) => t.isVisible).length > 0
            );
          }
          return true;
        })
        .map((stage) => (
          <>
            {stage._id && (
              <StageTaskList
                key={stage._id?.toString() ?? Math.random()}
                stage={stage}
                stageId={stage._id.toString()}
              />
            )}
          </>
        ))} */}
    </div>
  );
}

function TaskList({
  tasks,
  title,
  index,
}: {
  tasks: TaskWithChildren[];
  title: string;
  index: number;
}) {
  const app = useRealmApp();
  const journeyCtx = useJourney();
  // let sortedTasks = [...tasks];
  if (journeyCtx.state.group === "status") {
    tasks = tasks.sort((a, b) => {
      if (a.dueDate && b.dueDate) {
        if (a.updatedAt && b.updatedAt) {
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        } else if (b.updatedAt) {
          return -1;
        } else if (a.updatedAt) {
          return 1;
        }
      } else if (a.dueDate) {
        return -1;
      }
      return 1;
      return 0;
    });
  }
  return (
    <div>
      <div
        className={clsx(
          "flex h-[38px] min-w-0 items-center space-x-2 border-b border-gray-100 bg-gray-50 px-8",
          index !== 0 && "border-t border-gray-100"
        )}
      >
        <div className="min-w-0 max-w-full shrink-0 truncate text-sm text-black">
          {title}
        </div>
        {journeyCtx.state.group === "status" && (
          <div className="flex min-w-[16px] justify-center rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-600">
            {tasks.length}
          </div>
        )}
      </div>

      <div className="divide-y divide-gray-100">
        {tasks
          // .filter((t) => !t.parentTask)
          // .filter((t) => {
          //   if (journeyCtx.state.view.responsibility === "all") {
          //     return true;
          //   } else if (journeyCtx.state.view.responsibility === "internal") {
          //     return t.taskerType === "internal";
          //   } else if (journeyCtx.state.view.responsibility === "external") {
          //     return t.taskerType === "external";
          //   } else if (journeyCtx.state.view.responsibility === "my") {
          //     return (
          //       t.tasker.userType === "fullUser" &&
          //       t.tasker._id.toString() === app.currentUser.id
          //     );
          //   }
          // })
          // .filter((t) => {
          //   if (journeyCtx.state.view.showOnlyActiveAndIncompleteTasks) {
          //     // console.log(t.title, !!t.dueDate, t.status !== "Completed");
          //     return (
          //       !!t.dueDate &&
          //       t.status !== "Completed" &&
          //       t.status !== "Canceled"
          //     );
          //   } else {
          //     return true;
          //   }
          // })
          // .filter((t) => {
          //   if (journeyCtx.state.view.showInactive) {
          //     return true;
          //   } else {
          //     return !!t.dueDate;
          //   }
          // })
          // .filter((t) => {
          //   if (journeyCtx.state.view.showCompleted) {
          //     return true;
          //   } else {
          //     return t.status !== "Completed";
          //   }
          // })
          .filter((t) => {
            if (!journeyCtx.state.isExternal) return true;
            return t.isVisible;
          })
          // .filter((t) => {
          //   if (journeyCtx.state.view.showSubtasks) {
          //     return !t.parentTask;
          //   } else {
          //     return true;
          //   }
          // })
          .map((task) => (
            <>
              {!!task.parentTask?._id ? (
                <div className="pl-6">
                  <TaskWithSubTasks task={task} />
                </div>
              ) : (
                <TaskWithSubTasks task={task} />
              )}
            </>
          ))}
      </div>
    </div>
  );
}

function StageTaskList({
  stage,
  stageId,
  tasks,
}: {
  stage: JourneyStageObj;
  stageId: string;
  tasks: TaskWithChildren[];
}) {
  // const { data: tasks } = useGetJourneyStageTasks(
  //   stage.journeyId.toString(),
  //   stageId
  // );
  const app = useRealmApp();
  const journeyCtx = useJourney();
  return (
    <div>
      {!!tasks && tasks.length > 0 ? (
        <div className="divide-y divide-gray-100">
          <div className="flex items-center space-x-4 px-8 pb-2">
            <div className="text-sm font-medium">{stage.name}</div>
            {/* <div className="inline-flex items-center space-x-2 rounded-full border border-gray-400 px-2 py-1 text-xs text-gray-600">
              <>
                <WrenchIcon className="h-4 w-4 text-gray-500" />
              </>
              <div>
                <TotalTasksDuration tasks={tasks} />
              </div>
            </div> */}
          </div>
          {tasks
            // .filter((t) => !t.parentTask)
            // .filter((t) => {
            //   if (journeyCtx.state.view.responsibility === "all") {
            //     return true;
            //   } else if (journeyCtx.state.view.responsibility === "internal") {
            //     return t.taskerType === "internal";
            //   } else if (journeyCtx.state.view.responsibility === "external") {
            //     return t.taskerType === "external";
            //   } else if (journeyCtx.state.view.responsibility === "my") {
            //     return (
            //       t.tasker.userType === "fullUser" &&
            //       t.tasker._id.toString() === app.currentUser.id
            //     );
            //   }
            // })
            .filter((t) => {
              if (journeyCtx.state.view.showOnlyActiveAndIncompleteTasks) {
                // console.log(t.title, !!t.dueDate, t.status !== "Completed");
                return !!t.dueDate && t.status !== "Completed";
              } else {
                return true;
              }
            })
            .filter((t) => {
              if (journeyCtx.state.view.showInactive) {
                return true;
              } else {
                return !!t.dueDate;
              }
            })
            .filter((t) => {
              if (journeyCtx.state.view.showCompleted) {
                return true;
              } else {
                return t.status !== "Completed";
              }
            })
            .filter((t) => {
              if (!journeyCtx.state.isExternal) return true;
              return t.isVisible;
            })
            // .filter((t) => {
            //   if (journeyCtx.state.view.showSubtasks) {
            //     return !t.parentTask;
            //   } else {
            //     return true;
            //   }
            // })
            .map((task) => {
              return <TaskWithSubTasks task={task} />;
            })}
          <div />
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          <div className="px-8 pb-2 font-medium">{stage.name}</div>
          <div className="px-8 py-2 text-sm text-gray-400">
            No tasks in this stage
          </div>
        </div>
      )}
    </div>
  );
}

function TaskWithSubTasks({ task }: { task: TaskWithChildren }) {
  const journeyCtx = useJourney();
  // console.log(task)
  return (
    <div>
      <TaskRow task={task} isSubTask={false} />

      <>
        {!!task.subTaskCount && task.subTaskCount > 0 && (
          <div className="">
            {task.children
              ?.filter((t) => {
                if (!journeyCtx.state.isExternal) return true;
                return t.isVisible;
              })
              // .filter((t) => {
              //   if (journeyCtx.state.view.showOnlyActiveAndIncompleteTasks) {
              //     return !!t.dueDate && t.status !== "Completed";
              //   } else {
              //     return true;
              //   }
              // })
              .map((t) => (
                <TaskRow
                  task={t}
                  isSubTask={true}
                  key={t._id?.toString() ?? Math.random()}
                />
              ))}
          </div>
        )}
      </>
    </div>
  );
}

function TaskRow({
  task,
  isSubTask = false,
}: {
  task: TaskData;
  isSubTask?: boolean;
}) {
  const { phaseId } = useParams();
  const { data: settings } = useGetInstanceWorkingTimeSettings(String(phaseId));
  const journeyCtx = useJourney();
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const isTaskSelected = useIsTaskSelected(task._id ?? "");
  const { addTaskToSelection, removeTaskFromSelection } = useSelectionActions();

  useHotkeys(
    "x",
    () => {
      if (isHovered && task._id && task._id !== "-1") {
        if (isTaskSelected) {
          removeTaskFromSelection(task._id);
        } else {
          addTaskToSelection(task._id, task.dueDate ? "active" : "inactive");
        }
      }
    },
    [isHovered, isTaskSelected]
  );

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={clsx(
        "group relative flex h-10 items-center py-1.5 px-8 pl-16",
        "cursor-pointer hover:bg-gray-50",
        !task.dueDate && "opacity-50"
      )}
      key={task._id ? task._id.toString() : Math.random()}
    >
      <div
        className={clsx(
          "absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100",
          isTaskSelected && "opacity-100"
        )}
      >
        <Checkbox
          checked={isTaskSelected}
          onCheckedChange={() => {
            if (!task._id) return;
            if (isTaskSelected) {
              removeTaskFromSelection(task._id);
            } else {
              addTaskToSelection(
                task._id,
                task.dueDate ? "active" : "inactive"
              );
            }
          }}
        />
      </div>
      {/* {task.taskerType === "external" && (
        <div className="absolute right-0 mr-3 h-2 w-2 rounded-full bg-gray-400" />
      )} */}
      <div className="ml-0 flex items-center space-x-2">
        {isSubTask && <div className="w-8"></div>}
        {task.dueDate ? (
          <StatusSelect
            isProjectView={true}
            task={task}
            isSymbol={true}
            disabled={
              task.taskerType === "internal" && journeyCtx.state.isExternal
            }
            disabledStatuses={[
              ...(task.action === "form" || task.action === "fileUpload"
                ? ["Completed" as TaskData["status"]]
                : []),
              ...(journeyCtx.state.isExternal
                ? ["Canceled" as TaskData["status"]]
                : []),
              "Backlog",
            ]}
            isTag={true}
          />
        ) : (
          // <Status
          //   disabled={
          //     task.taskerType === "internal" && journeyCtx.state.isExternal
          //   }
          //   isButtonIcon={true}
          //   onChange={(status) => {
          //     if (task.journeyStage?._id && task.journey?._id) {
          //       if (!task._id) return;
          //       updateTaskMutation.mutate({
          //         update: { _id: task._id, status: status },
          //         metadata: {
          //           phaseId: task?.phase?._id,
          //           journeyId: task?.journey?._id,
          //           journeyStageId: task?.journeyStage?._id,
          //         },
          //       });
          //     }
          //   }}
          //   status={task.status}
          //   isBacklogAvailable={false}
          // />
          <>
            <CircleIcon className="h-4 w-4 text-gray-300" />
            {/* {!task.dueDate && (
              <div
                className="flex flex-shrink items-center truncate rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500"
                onClick={() => {
                  if (task._id) {
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.set("taskId", task._id);
                    navigate({
                      pathname: location.pathname,
                      search: searchParams.toString(),
                    });
                  }
                }}
              >
                <span className="whitespace-pre font-light">{`Dependent on completion of `}</span>
                {journeyCtx.state.isExternal ? (
                  <span className="flex-shrink truncate font-light">
                    another task
                  </span>
                ) : (
                  <>
                    <span className="whitespace-pre font-light">{`${
                      task.dependentOn?.type === "stage" ? "stage" : "task"
                    } `}</span>
                    <span className="flex-shrink truncate font-medium">
                      {task.dependentOn?.name}
                    </span>
                  </>
                )}
              </div>
            )} */}
          </>
        )}
      </div>

      <div
        className="flex min-w-0 items-center space-x-4 pl-2"
        onClick={() => {
          if (task._id && task._id !== "-1") {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("taskId", task._id);
            navigate({
              pathname: location.pathname,
              search: searchParams.toString(),
            });
          }
        }}
      >
        <div
          className={clsx(
            "truncate text-sm",
            isSubTask ? "text-gray-500" : "text-gray-700"
          )}
        >
          {task.title}
        </div>
        {/* {task.isMarkedForToday && (
      <CalendarIcon className="h-5 w-5 text-primary-main" />
    )} */}
        {task?.subTaskCount && task.subTaskCount > 0 && (
          <>
            <div className="inline-flex items-center space-x-0.5 rounded-full border border-gray-400 py-0.5 px-1.5 text-gray-400">
              <span className="text-xs">{task.completedSubTaskCount}</span>
              <span className="text-xs">{`/`}</span>
              <span className="text-xs">{task.subTaskCount}</span>
              {/* <BoltIcon className="h-3 w-3" /> */}
            </div>
          </>
        )}
        {/* {task.subTaskCount && (
      <div
        onClick={() => {
          
          setOpen && setOpen(!open);
        }}
        className="border"
      >
        <ChevronDownIcon className="h-3 w-3 text-primary-main" />
      </div>
    )} */}
        {task.parentTask && !isSubTask && (
          <div className="flex items-center space-x-4">
            <ChevronRightIcon className="h-3 w-3 text-gray-400" />
            <span className="text-sm text-gray-400">
              {task.parentTask.title}
            </span>
          </div>
        )}
      </div>
      <div
        className="flex flex-grow items-center justify-end space-x-6"
        onClick={() => {
          if (task._id) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("taskId", task._id);
            navigate({
              pathname: location.pathname,
              search: searchParams.toString(),
            });
          }
        }}
      >
        {/* {task.estimate && (
      <div className="inline-flex items-center space-x-2 rounded-full border px-2 py-1 text-xs text-gray-600">
        <>
          <WrenchIcon className="h-4 w-4 text-gray-500" />
        </>
        <div>{task.estimate}</div>
      </div>
    )} */}

        {/* {task.phase && (
      <div
        className="rounded-full border border-gray-400 px-2 py-1 text-xs text-gray-500 hover:border-gray-700 hover:bg-gray-700 hover:text-white"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/projects/${task.phase?._id.toString()}/journey`);
        }}
      >
        {task.journey?.name}
      </div>
    )} */}
        {/* {task.journey && (
      <Chip label={task.journey.name} size="small" variant="outlined" />
    )} */}

        {/* {task.dueDate && (
          <TimeLeft
            dueDate={task.dueDate}
            completionDate={task.completionDate}
            task={task}
          />
        )} */}

        <div className="flex w-[100px] justify-end">
          {task.taskerType === "external" && (
            <div className="max-w-[100px] truncate rounded border bg-gray-200/70 px-1 text-xs text-gray-800">
              {task.customer?.name}
            </div>
          )}
        </div>
        <div className="w-5">
          {!task.isVisible && (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <EyeSlashIcon
                    className={clsx(
                      "h-[18px] w-[18px] shrink-0 text-gray-400/80"
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent sideOffset={8}>
                  <p>Externally hidden</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="w-5">
          {task.isMilestone && (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <MilestoneIcon />
                </TooltipTrigger>
                <TooltipContent sideOffset={8}>
                  <p>Milestone</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {/* <div className="w-12">
          {task.estimate && !task.subTaskCount && (
            <div className="text-sm">
              <span className="text-gray-600">
                {task.estimate.unit === "hour"
                  ? task.estimate.value * 60
                  : task.estimate.value}
              </span>
              <span className="text-xs text-gray-500"> min</span>
            </div>
          )}
        </div> */}

        {task.dueDate ? (
          <span
            className={clsx(
              "w-14 text-right text-sm",
              task.dueDate && dayjs(task.dueDate).isBefore(new Date())
                ? "text-red-500"
                : "text-gray-400"
              // className="w-14 text-right text-sm text-gray-500">
            )}
          >
            {dayjs(task.dueDate).format("DD MMM")}
          </span>
        ) : (
          <div className="flex w-14 justify-end">
            {!!task.durations?.forecasted && !!settings && (
              <span className="w-14 text-right text-sm text-gray-500">
                {dayjs(
                  calculateEndDate(
                    new Date(),
                    task.durations.forecasted,
                    settings?.workingTimeDetails,
                    settings?.timezone
                  )
                ).format("DD MMM")}
              </span>
            )}
          </div>
        )}

        <div className="flex w-32 justify-start">
          <div
            className={clsx(
              "w-fit truncate whitespace-nowrap rounded-full border px-2 py-0.5 text-xs",
              task.tasker.userType === "role"
                ? "border-dashed border-red-400 text-red-600"
                : task.taskerType === "internal"
                ? "border-gray-200 bg-white text-gray-500"
                : "border-gray-300 bg-gray-100 text-gray-700"
            )}
          >
            {task.tasker.userType === "role" ||
            task.tasker.userType === "persona"
              ? "Unassigned"
              : getIdentifier(task.tasker).identifier}
          </div>
          {/* <Avatar
            name={getIdentifier(task.tasker).identifier}
            size="sm"
            color={task.taskerType === "external" ? "slate" : "gray"}
            fill={task.taskerType === "external" ? "solid" : "outline"}
          /> */}
        </div>
      </div>
    </div>
  );
}

const KanbanView = React.memo(
  ({
    stages,
    journeyId,
    statusGroup,
    tasks,
    phase,
  }: {
    tasks: TaskWithChildren[];
    stages: JourneyStageObj[];
    journeyId: string;
    statusGroup: StatusGroupData;
    phase: PhaseData;
  }) => {
    const kanbanTasks = useMemo(() => {
      return tasks.filter((t) => !t.parentTask?._id);
    }, [tasks]);
    const journeyCtx = useJourney();
    const statuses: Record<ParentStatus, string[]> = useMemo(() => {
      return {
        "To-do": statusGroup.statuses
          .filter((s) => s.parentStatus === "To-do")
          .map((s) => s.name),
        "In Progress": statusGroup.statuses
          .filter((s) => s.parentStatus === "In Progress")
          .map((s) => s.name),
        Stuck: statusGroup.statuses
          .filter((s) => s.parentStatus === "Stuck")
          .map((s) => s.name),
        Completed: statusGroup.statuses
          .filter((s) => s.parentStatus === "Completed")
          .map((s) => s.name),
        Canceled: statusGroup.statuses
          .filter((s) => s.parentStatus === "Canceled")
          .map((s) => s.name),
        Backlog: statusGroup.statuses
          .filter((s) => s.parentStatus === "Backlog")
          .map((s) => s.name),
      };
    }, [statusGroup]);
    const memoizedJourneyObjectId = useMemo(
      () => new ObjectID(journeyId),
      [journeyId]
    );
    const { journey } = useGetJourney(memoizedJourneyObjectId);
    const tasksByStageId = useMemo(() => {
      if (!kanbanTasks || !journey?.stageOrder) {
        return new Map<string, TaskWithChildren[]>();
      }
      const map = new Map<string, TaskWithChildren[]>();
      for (const stageIdObj of journey.stageOrder) {
        const stageIdStr = stageIdObj.toString(); // Ensure consistent key type
        const filteredTasks = kanbanTasks.filter(
          (task) => task.journeyStage?._id === stageIdStr
        );
        map.set(stageIdStr, filteredTasks);
      }
      return map;
    }, [kanbanTasks, journey?.stageOrder]);

    const stagesToShow = useMemo(() => {
      if (journeyCtx.state.isExternal) {
        return stages.filter((s) => {
          if (journeyCtx.state.isExternal) {
            return (
              !s.tasks?.filter((t) => !t.isVisible).length ||
              s.tasks.filter((t) => t.isVisible).length > 0
            );
          }
          return true;
        });
      }
      return stages;
    }, [stages, journeyCtx.state.isExternal, journey]);

    if (!journey) {
      return null;
    }

    // console.log(journey.stageOrder.map((s) => s.toString()));

    if (journeyCtx.state.group === "stage") {
      return (
        <div className="flex h-full gap-4 overflow-auto px-8 pt-4">
          {journey?.stageOrder.map((stageId) => {
            const stage = stagesToShow.find(
              (s) => s._id?.toString() === stageId.toString()
            );
            if (!stage) {
              return null;
            }
            const currentStageTasks =
              tasksByStageId.get(stageId.toString()) || []; // Get the memoized list

            return (
              <TasksGroup
                journey={journey}
                key={stage._id?.toString()}
                tasks={currentStageTasks}
                title={stage.name}
                stage={stage}
                phase={phase}
                journeyId={journeyId}
              />
            );
          })}
          {/* stages
          .filter((s) => {
            if (journeyCtx.state.isExternal) {
              return (
                !s.tasks?.filter((t) => !t.isVisible).length ||
                s.tasks.filter((t) => t.isVisible).length > 0
              );
            }
            return true;
          })
          .map((stage) => (
            <TasksGroup
              tasks={tasks.filter(
                (t) => t.journeyStage?._id === stage._id?.toString()
              )}
              title={stage.name}
              stage={stage}
              phase={phase}
              journeyId={journeyId}
            />
          ))} */}
          {stages.length > 0 && <AddStageButton journeyId={journeyId} />}
        </div>
      );
    } else if (journeyCtx.state.group === "status") {
      // iterate through each status
      let groups: JSX.Element[] = [];
      for (const [key, value] of Object.entries(statuses)) {
        groups.push(
          ...value.map((customStatus) => {
            return (
              <TasksGroup
                tasks={kanbanTasks?.filter(
                  (t) => t.customStatus === customStatus && t.status === key
                )}
                title={customStatus}
                phase={phase}
                journeyId={journeyId}
                journey={journey}
              />
            );
          })
        );
      }
      return (
        <div className="flex h-full gap-4 overflow-auto px-8 pt-4">
          {groups}
        </div>
      );
    } else {
      return <></>;
    }

    return (
      <div className="relative z-0 flex h-full flex-1 overflow-x-auto overflow-y-auto px-2 pt-6">
        {!!stages &&
          stages
            .filter((s) => {
              if (journeyCtx.state.isExternal) {
                return (
                  !s.tasks?.filter((t) => !t.isVisible).length ||
                  s.tasks.filter((t) => t.isVisible).length > 0
                );
              }
              return true;
            })
            .map((stage) => (
              <div
                key={stage._id ? stage._id.toString() : Math.random()}
                className="flex shrink-0 flex-col overflow-y-auto"
              >
                <JourneyStage stage={stage} />
              </div>
            ))}
      </div>
    );
  }
);

function AddStageButton({ journeyId }: { journeyId: string }) {
  const [newStageName, setNewStageName] = useState("");
  const addStageMutation = useAddStage();
  useEffect(() => {
    if (addStageMutation.isSuccess) {
      setNewStageName("");
    }
  }, [addStageMutation.isSuccess]);
  return (
    <div className="min-w-[312px]">
      <Dialog>
        <DialogTrigger asChild>
          <Button
            size="lg"
            className="h-10 w-full border-dashed"
            variant={"secondary"}
          >
            {/* <PlusIcon className="h-5 w-5 text-gray-500" /> */}
            <span className="text-sm text-gray-500">Add Stage</span>
          </Button>
        </DialogTrigger>
        <DialogContent
          className={clsx(
            "top-[20%] w-full max-w-lg translate-y-0 data-[state=open]:slide-in-from-top-8"
          )}
        >
          <DialogHeader>
            <DialogTitle>New Stage</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1">
              <InputHeading heading="Name" />
              <TextInput
                className="max-w-[300px]"
                value={newStageName}
                onChange={(e) => {
                  setNewStageName(e.target.value);
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                disabled={!newStageName}
                onClick={() => {
                  // todo: add stage
                  addStageMutation.mutate({
                    params: {
                      name: newStageName,
                      journeyId,
                    },
                  });
                }}
              >
                Create
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TasksGroup = React.memo(
  ({
    tasks,
    title,
    stage,
    phase,
    journeyId,
    journey,
  }: {
    tasks: TaskWithChildren[];
    title: string;
    stage?: JourneyStageObj;
    phase: PhaseData;
    journeyId: string;
    journey: JourneyData;
  }) => {
    const stageHoverActions = useStageHoverActions();
    const app = useRealmApp();
    const [open, setOpen] = useState(false);
    const { slug, phaseId } = useParams();
    const journeyCtx = useJourney();
    const newTaskForExternalUserCtx = useNewTaskForExternalUser();
    const newTaskForInternalUserCtx = useNewTaskForInternalUser();
    const deleteJourneyStageMutation = useDeleteJourneyStage();
    const [newStageName, setNewStageName] = useState(stage?.name ?? "");
    const [isEditing, setIsEditing] = useState(false);
    const updateJourneyStageMutation = useUpdateJourneyStage();
    // let sortedTasks = [...tasks];
    const [isReordering, setIsReordering] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    useEffect(() => {
      if (stage?.name) {
        setNewStageName(stage?.name);
      }
    }, [stage?.name]);
    const sizeMap = useRef<{ [idx: number]: number }>({});
    const getSize = (index: number) => sizeMap.current[index] ?? 180;
    const listRef = useRef<VariableSizeList<any> | null>(null);

    const handleMouseEnter = useCallback(() => {
      if (journeyCtx.state.group === "stage" && stage?._id) {
        stageHoverActions.setHoveredStageId(stage._id.toString());
      }
    }, [journeyCtx.state.group, stage, stageHoverActions.setHoveredStageId]);

    const handleMouseLeave = useCallback(() => {
      if (journeyCtx.state.group === "stage" && stage?._id) {
        stageHoverActions.setHoveredStageId(null);
      }
    }, [journeyCtx.state.group, stage, stageHoverActions.setHoveredStageId]);

    // const attachListRef = useCallback((instance: VariableSizeList | null) => {
    //   listRef.current = instance;
    // }, []);
    const setListRef = useCallback((instance: VariableSizeList | null) => {
      listRef.current = instance;
    }, []);

    const setItemSize = useCallback((index: number, height: number) => {
      if (sizeMap.current[index] !== height) {
        sizeMap.current[index] = height;
        // Reset from this index onwards
        listRef.current?.resetAfterIndex(index, true);
      }
    }, []);

    const [ready, setReady] = useState(false);

    const measurables = useMemo(() => tasks.slice(0, 20), [tasks]);
    const countToMeasure = useMemo(() => measurables.length, [measurables]);

    useEffect(() => {
      if (!ready && Object.keys(sizeMap.current).length === countToMeasure) {
        setReady(true);
      }
    }, [ready, sizeMap.current]);

    // 1) Offscreen measurer
    const measureContainer = useMemo(
      () => (
        <div
          style={{ position: "absolute", visibility: "hidden", width: "312px" }}
        >
          {tasks.slice(0, 20).map((task, i) => (
            <MeasuredCard
              key={task._id}
              task={task}
              onResize={(h) => (sizeMap.current[i] = h)}
              onAllMeasured={() => setReady(true)}
            />
          ))}
        </div>
      ),
      [tasks]
    );

    // 2) Only show when ready
    if (!ready) return measureContainer;

    if (stage?.isUnassigned && tasks.length === 0) {
      return null;
    }
    // if (journeyCtx.state.group === "status") {
    //   sortedTasks = sortedTasks.sort((a, b) => {
    //     if (a.dueDate && b.dueDate) {
    //       if (a.updatedAt && b.updatedAt) {
    //         return (
    //           new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    //         );
    //       } else if (b.updatedAt) {
    //         return -1;
    //       } else if (a.updatedAt) {
    //         return 1;
    //       }
    //     } else if (a.dueDate) {
    //       return -1;
    //     }
    //     return 1;
    //     return 0;
    //   });
    // }

    return (
      <div
        className="flex h-full min-w-[312px] max-w-[312px] flex-1 flex-col rounded-t-lg bg-gradient-to-b from-gray-50 to-gray-50/50"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="">
          <div className="group flex items-center justify-between gap-1 rounded-md pl-5 pr-3.5 pt-3.5 pb-3.5">
            <div className="flex min-w-0 items-center space-x-2">
              <div className="min-w-0 max-w-full shrink-0 truncate text-sm">
                {title}
              </div>
              {journeyCtx.state.group === "status" && (
                <div className="flex min-w-[16px] justify-center rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-600">
                  {tasks.length}
                </div>
              )}
            </div>
            <div className="group flex items-center">
              {!!stage?._id && !stage.isUnassigned && (
                <DropdownMenu
                  open={open}
                  onOpenChange={(isOpen) => {
                    setOpen(isOpen);
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={"ghost"}
                      className={clsx(
                        "h-7 w-7 p-0 hover:bg-gray-200/60 group-hover:visible",
                        !open ? "invisible" : "bg-gray-200/60"
                      )}
                    >
                      <EllipsisHorizontalIcon className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-44" align="end">
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        // className="py-1 focus:bg-gray-50 focus:text-red-600"
                        onClick={(e) => {
                          setIsEditing(true);
                        }}
                      >
                        <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="ml-2 text-sm">Edit</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        // className="py-1 focus:bg-gray-50 focus:text-red-600"
                        onClick={(e) => {
                          setIsReordering(true);
                        }}
                      >
                        <ArrowsRightLeftIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="ml-2 text-sm">Reorder</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        // className="py-1 focus:bg-gray-50 focus:text-red-600"
                        className="focus:bg-red-50 focus:text-red-600"
                        onClick={(e) => {
                          setIsDeleting(true);
                          e.stopPropagation();
                        }}
                      >
                        <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="ml-2 text-sm">Delete</span>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <Button
                variant={"ghost"}
                className="h-7 w-7 hover:bg-gray-200/60"
                onClick={() => {
                  if (journeyCtx.state.isExternal) {
                    newTaskForExternalUserCtx.dispatch({
                      type: "open_update",
                      projectId: phase._id,
                      stageId: stage?._id?.toString(),
                    });
                  } else {
                    newTaskForInternalUserCtx.dispatch({
                      type: "open_update",
                      projectId: phase._id,
                      stageId: stage?._id?.toString(),
                    });
                  }
                }}
              >
                <PlusIcon className="h-3.5 w-3.5 shrink-0" />
              </Button>
              <Dialog
                open={isEditing}
                onOpenChange={(open) => {
                  setIsEditing(open);
                }}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Editing Stage</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-1">
                      <InputHeading heading="Name" />
                      <TextInput
                        value={newStageName}
                        onChange={(e) => {
                          setNewStageName(e.target.value);
                        }}
                        onBlur={() => {
                          if (newStageName.trim() === "") {
                            setNewStageName(stage?.name ?? "");
                          } else if (
                            newStageName.trim() !== stage?.name.trim()
                          ) {
                            if (stage && stage._id) {
                              // update stage name
                              updateJourneyStageMutation.mutate({
                                params: {
                                  _id: stage._id,
                                  name: newStageName,
                                },
                                metadata: {
                                  journeyId: stage.journeyId.toString(),
                                },
                              });
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog
                open={isDeleting}
                onOpenChange={(open) => {
                  setIsDeleting(open);
                }}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete Stage</DialogTitle>
                  </DialogHeader>
                  <div className="py-2">
                    <div className="text-sm text-gray-600">
                      Are you sure you want to delete the stage{" "}
                      <span className="font-medium text-gray-900">
                        {stage?.name}
                      </span>
                      ?
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2.5">
                    <Button
                      variant={"secondary"}
                      onClick={() => setIsDeleting(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={"destructive"}
                      onClick={() => {
                        if (stage?._id) {
                          deleteJourneyStageMutation.mutate({
                            params: {
                              _id: stage._id,
                            },
                            metadata: {
                              phaseId: String(phaseId),
                              journeyId: journey._id.toString(),
                            },
                          });
                        }
                      }}
                      disabled={deleteJourneyStageMutation.isLoading}
                    >
                      {deleteJourneyStageMutation.isLoading
                        ? "Deleting..."
                        : "Delete"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog
                open={isReordering}
                onOpenChange={(open) => {
                  setIsReordering(open);
                }}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Stage Order</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Reordering journeyId={journeyId} journey={journey} />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-1">
          {/* {sortedTasks
          // .filter((t) =>
          //   journeyCtx.state.view.view === "list" ? !t.parentTask : true
          // )
          .filter((t) => {
            if (journeyCtx.state.view.responsibility === "all") {
              return true;
            } else if (journeyCtx.state.view.responsibility === "internal") {
              return t.taskerType === "internal";
            } else if (journeyCtx.state.view.responsibility === "external") {
              return t.taskerType === "external";
            } else if (journeyCtx.state.view.responsibility === "my") {
              return (
                t.tasker.userType === "fullUser" &&
                t.tasker._id.toString() === app.currentUser.id
              );
            }
          })
          .filter((t) => {
            if (journeyCtx.state.view.showOnlyActiveAndIncompleteTasks) {
              // console.log(t.title, !!t.dueDate, t.status !== "Completed");
              return (
                !!t.dueDate &&
                t.status !== "Completed" &&
                t.status !== "Canceled"
              );
            } else {
              return true;
            }
          })
          // .filter((t) => {
          //   if (journeyCtx.state.view.showInactive) {
          //     return true;
          //   } else {
          //     return !!t.dueDate;
          //   }
          // })
          // .filter((t) => {
          //   if (journeyCtx.state.view.showCompleted) {
          //     return true;
          //   } else {
          //     return t.status !== "Completed";
          //   }
          // })
          .filter((t) => {
            if (!journeyCtx.state.isExternal) return true;
            return t.isVisible;
          })
          // .filter((t) => {
          //   if (journeyCtx.state.view.showSubtasks) {
          //     return !t.parentTask;
          //   } else {
          //     return true;
          //   }
          // }) */}
          <KanbanColumnTaskList
            tasks={tasks}
            listRef={listRef}
            setListRef={setListRef}
            sizeMap={sizeMap}
            setItemSize={setItemSize}
            getSize={getSize}
          />
          {/* {tasks.map((task) => (
            <TaskCard task={task} />
          ))} */}
          {/* {tasks.slice(0, 1).map((task) => (
            <TaskCard task={task} />
          ))} */}
          {/* {tasks.map((task) => (
          <TaskCard task={task} />
          // <>
          //   {!!task.parentTask?._id ? (
          //     <div className="pl-6">
          //     </div>
          //   ) : (
          //     <TaskCard task={task} />
          //   )}
          // </>
        ))} */}
        </div>
      </div>
    );
  }
);

const KanbanColumnTaskList = React.memo(
  ({
    tasks,
    listRef,
    setListRef,
    sizeMap,
    setItemSize,
    getSize,
  }: {
    tasks: TaskWithChildren[];
    setListRef: (instance: VariableSizeList | null) => void;
    listRef: React.RefObject<VariableSizeList>;
    sizeMap: React.MutableRefObject<{ [idx: number]: number }>;
    setItemSize: (index: number, size: number) => void;
    getSize: (index: number) => number;
  }) => {
    return (
      <AutoSizer>
        {({ height, width }: { height: number; width: number }) => (
          <VariableSizeList
            ref={setListRef}
            // innerRef={listRef}
            height={height}
            width={width}
            itemCount={tasks.length}
            itemSize={(idx) => getSize(idx)}
            overscanCount={10}
            className="pb-6"
            itemKey={(idx) => tasks[idx]._id || idx}
          >
            {({ index, style }) => {
              const task = tasks[index];
              // const subtasks = childrenByParent[task.id] || [];
              return (
                <div style={style} key={task._id}>
                  <NewTaskCard
                    task={task}
                    listRef={listRef}
                    sizeMap={sizeMap}
                    index={index}
                    setItemSize={setItemSize}
                  />
                </div>
              );
            }}
          </VariableSizeList>
        )}
      </AutoSizer>
    );
  }
);

function MeasuredCard({
  task,
  onResize,
  onAllMeasured,
}: {
  task: TaskWithChildren;
  onResize: (height: number) => void;
  onAllMeasured: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const measureHeight = () => {
      if (ref.current) {
        const height = ref.current.getBoundingClientRect().height;
        onResize(height);
      }
    };
    measureHeight();
  }, [task]); // Include task to remeasure when task changes

  return (
    <div ref={ref} className="space-y-1 pt-1">
      <TaskCard task={task} />
      {task.children?.length > 0 && (
        <div className="space-y-1 pl-6">
          {task.children?.map((child) => (
            <TaskCard key={child._id} task={child} />
          ))}
        </div>
      )}
    </div>
  );
}

function NewTaskCard({
  task,
  listRef,
  sizeMap,
  index,
  setItemSize,
}: {
  task: TaskWithChildren;
  listRef: React.RefObject<VariableSizeList>;
  sizeMap: React.MutableRefObject<{ [idx: number]: number }>;
  index: number;
  setItemSize: (index: number, size: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const measureHeight = () => {
      if (ref.current) {
        const height = ref.current.getBoundingClientRect().height;
        setItemSize(index, height);
      }
    };
    measureHeight();
  }, [index, setItemSize, task]); // Include task to remeasure when task changes
  return (
    <div ref={ref} className="space-y-1 pt-1">
      <TaskCard task={task} />
      {task.children?.length > 0 && (
        <div className="space-y-1 pl-6">
          {task.children?.map((child) => (
            <TaskCard key={child._id} task={child} />
          ))}
        </div>
      )}
    </div>
  );
}

function Reordering({
  journeyId,
  journey,
}: {
  journeyId: string;
  journey: JourneyData;
}) {
  const { stages } = useGetJourneyStages(new ObjectID(journeyId));
  const updateJourney = useUpdateJourney();
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    let oldIndex: number;
    let newIndex: number;
    if (!over) return;
    if (active.id !== over.id) {
      oldIndex = journey.stageOrder.findIndex(
        (s) => s.toString() === active.id
      );
      newIndex = journey.stageOrder.findIndex((s) => s.toString() === over.id);
      const newItems = arrayMove(journey.stageOrder, oldIndex, newIndex);
      updateJourney.mutate({
        params: {
          _id: journey._id,
          stageOrder: newItems,
        },
      });
    }
  }
  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext
        items={
          journey.stageOrder
            .filter(
              (s) =>
                s.toString() !==
                stages?.find((s) => s.isUnassigned)?._id?.toString()
            )
            .map((s) => {
              return { id: s.toString() };
            }) ?? []
        }
        strategy={verticalListSortingStrategy}
      >
        {journey.stageOrder
          .filter(
            (s) =>
              s.toString() !==
              stages?.find((s) => s.isUnassigned)?._id?.toString()
          )
          .map((stageId) => {
            const stage = stages?.find(
              (s) => s._id?.toString() === stageId.toString()
            );
            if (!stage) {
              return null;
            }
            return <SortableItem key={stageId.toString()} stage={stage} />;
          })}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ stage }: { stage: JourneyStageObj }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      animateLayoutChanges: () => false,
      id: stage._id?.toString() ?? "",
    });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  return (
    <div
      style={style}
      {...attributes}
      ref={setNodeRef}
      className="group flex cursor-default items-center pl-1.5"
      tabIndex={-1}
    >
      <div
        className="mr-1.5 cursor-grab rounded p-1 hover:bg-gray-100"
        {...listeners}
      >
        <DragHandleDots2Icon className="h-4 w-4 text-gray-500" />
      </div>
      <div>{stage.name}</div>
    </div>
  );
}

type RoleMapping = {
  roleName: string;
  type: "internal" | "external";
  roleId: string;
  email?: string;
  name?: string;
};

function RoleAssignment({ phaseId }: { phaseId: string }) {
  const { data: phase } = useGetPhase(phaseId);
  const [isOpen, setIsOpen] = useState(false);
  const [roleMappings, setRoleMappings] = useState<RoleMapping[]>([]);
  const missingRoles = useMemo(() => {
    return (
      (phase?.internalRoles?.length ?? 0) + (phase?.externalRoles?.length ?? 0)
    );
  }, [phase?.internalRoles, phase?.externalRoles]);
  useEffect(() => {
    setRoleMappings([
      ...(phase?.internalRoles?.map((r) => ({
        roleName: r.name,
        roleId: r._id,
        email: undefined,
        type: "internal" as "internal",
      })) ?? []),
      ...(phase?.externalRoles?.map((r) => ({
        roleName: r.name,
        roleId: r._id,
        email: undefined,
        type: "external" as "external",
      })) ?? []),
    ]);
  }, [phase?.internalRoles, phase?.externalRoles]);
  const assignUserToTasksWithRoleInProjectMutation =
    useAssignUserToTasksWithRoleInProject();
  useEffect(() => {
    if (assignUserToTasksWithRoleInProjectMutation.isSuccess) {
      setIsOpen(false);
    }
  }, [assignUserToTasksWithRoleInProjectMutation.isSuccess]);
  const { data: projectMembers } = useGetProjectMembers(phaseId);
  if (!phase) return null;
  if (!phase.internalRoles?.length && !phase.externalRoles?.length) {
    return null;
  }

  return (
    <>
      <Button
        variant={"secondary"}
        className="border-dashed border-red-500 font-normal text-red-400"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <ExclamationCircleIcon className="mr-1.5 h-[18px] w-[18px]" />
        {missingRoles} Unassigned Role{missingRoles > 1 ? "s" : ""}
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Roles</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4 pb-6">
            {roleMappings.filter((rm) => rm.type === "internal").length > 0 && (
              <div className="space-y-2">
                <div className="space-y-1">
                  <div className="text-sm font-light text-gray-800">
                    Internal Roles
                  </div>
                  {!projectMembers?.internal.length && (
                    <div className="max-w-sm pb-1 text-xs font-light text-red-600">
                      No internal members available to assign to roles. Please
                      add internal members to the project first.
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {roleMappings
                    .filter((rm) => rm.type === "internal")
                    .map((rm) => (
                      <RoleMappingRow
                        roleMapping={rm}
                        roleMappings={roleMappings}
                        phaseId={phaseId}
                        setRoleMappings={setRoleMappings}
                        type="internal"
                      />
                    ))}
                </div>
              </div>
            )}
            {roleMappings.filter((rm) => rm.type === "external").length > 0 && (
              <div className="space-y-2">
                <div className="space-y-1">
                  <div className="text-sm font-light text-gray-800">
                    External Roles
                  </div>
                  {!projectMembers?.external.length && (
                    <div className="max-w-sm pb-1 text-xs font-light text-red-600">
                      No external members available to assign to roles. Please
                      invite external members to the project first.
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {roleMappings
                    .filter((rm) => rm.type === "external")
                    .map((rm) => (
                      <RoleMappingRow
                        roleMapping={rm}
                        roleMappings={roleMappings}
                        phaseId={phaseId}
                        setRoleMappings={setRoleMappings}
                        type="external"
                      />
                    ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant={"secondary"} onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                !roleMappings.some((rm) => !!rm.email) ||
                assignUserToTasksWithRoleInProjectMutation.isLoading
              }
              onClick={() => {
                assignUserToTasksWithRoleInProjectMutation.mutate({
                  phaseId,
                  assignments: roleMappings
                    .filter((rm) => !!rm.email)
                    .map((rm) => ({
                      role: { _id: rm.roleId },
                      email: rm.email ?? "",
                    })),
                });
              }}
            >
              {assignUserToTasksWithRoleInProjectMutation.isLoading
                ? "Saving..."
                : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RoleMappingRow({
  roleMapping,
  roleMappings,
  phaseId,
  setRoleMappings,
  type,
}: {
  roleMapping: RoleMapping;
  roleMappings: RoleMapping[];
  phaseId: string;
  setRoleMappings: (rm: RoleMapping[]) => void;
  type: "internal" | "external";
}) {
  const { data: projectMembers } = useGetProjectMembers(phaseId);
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-gray-50 p-2 pl-3">
      <div className="text-sm font-medium">{roleMapping.roleName}</div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={
              type === "internal"
                ? !projectMembers?.internal.length
                : !projectMembers?.external.length
            }
            variant="secondary"
            role="combobox"
            aria-expanded={open}
            className="max-w-[200px] space-x-2 truncate px-2"
          >
            <UserIcon className="h-3.5 w-3.5 shrink-0" />

            {roleMapping.email ? (
              <>
                <div className="truncate">
                  {roleMapping.name ?? roleMapping.email}
                </div>
              </>
            ) : (
              <div className="text-red-600">Unassigned</div>
            )}
            <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search assignee..." className="h-9" />
            <CommandList>
              <CommandEmpty>No assignee found.</CommandEmpty>
              <CommandGroup>
                {type === "internal" && (
                  <>
                    {projectMembers?.internal.map((internalTasker) => (
                      <CommandItem
                        key={internalTasker.email}
                        value={internalTasker.name}
                        onSelect={() => {
                          setRoleMappings(
                            roleMappings.map((rm) =>
                              roleMapping.roleId === rm.roleId
                                ? {
                                    ...rm,
                                    email: internalTasker.email,
                                    name: internalTasker.name,
                                  }
                                : rm
                            )
                          );
                          setOpen(false);
                        }}
                        className="gap-2"
                      >
                        <div className="flex items-center space-x-2">
                          <div
                            className={clsx(
                              "h-2 w-2 shrink-0 rounded-full bg-gray-300"
                            )}
                          />
                          <span className="truncate">
                            {internalTasker.name}
                          </span>
                        </div>
                        <CheckIcon
                          className={cn(
                            "ml-auto h-4 w-4 shrink-0",
                            internalTasker.email === roleMapping.email
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </>
                )}
                {type === "external" && (
                  <>
                    {projectMembers?.external.map((externalTasker) => (
                      <CommandItem
                        key={externalTasker.email}
                        value={
                          externalTasker.userType === "fullUser"
                            ? externalTasker.name
                            : externalTasker.email
                        }
                        onSelect={() => {
                          if (externalTasker.userType === "fullUser") {
                            setRoleMappings(
                              roleMappings.map((rm) =>
                                roleMapping.roleId === rm.roleId
                                  ? {
                                      ...rm,
                                      email: externalTasker.email,
                                      name: externalTasker.name,
                                    }
                                  : rm
                              )
                            );
                          } else {
                            setRoleMappings(
                              roleMappings.map((rm) =>
                                roleMapping.roleId === rm.roleId
                                  ? {
                                      ...rm,
                                      email: externalTasker.email,
                                    }
                                  : rm
                              )
                            );
                          }
                          setOpen(false);
                        }}
                        className="gap-2"
                      >
                        <div className="flex items-center space-x-2">
                          <div
                            className={clsx(
                              "h-2 w-2 shrink-0 rounded-full bg-slate-700"
                            )}
                          />
                          <span className="truncate">
                            {externalTasker.userType === "fullUser"
                              ? externalTasker.name
                              : externalTasker.email}
                          </span>
                        </div>
                        <CheckIcon
                          className={cn(
                            "ml-auto h-4 w-4 shrink-0",
                            roleMapping.email === externalTasker.email
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TasksAssignment({ phaseId }: { phaseId: string }) {
  const { data: tasks } = useGetPhaseTasks(phaseId);
  return <></>;
  // return <div className="bg-red-500">unassigned tasks button</div>;
  // return <>{!!tasks && <TasksAssignmentButton tasks={tasks} />}</>;
}

// function TasksAssignmentButton({ tasks }: { tasks: TaskData[] }) {
//   const { slug } = useParams();
//   let unassginedTasks = tasks.filter((t) => t.tasker.userType === "persona");
//   if (!!slug) {
//     unassginedTasks = unassginedTasks.filter(
//       (t) => t.taskerType === "external"
//     );
//   }
//   const [isOpen, setIsOpen] = useState(false);
//   return (
//     <>
//       {unassginedTasks.length > 0 && (
//         <button
//           className="inline-flex space-x-2 rounded-md border border-red-500 px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50"
//           onClick={() => {
//             setIsOpen(true);
//           }}
//         >
//           <ExclamationCircleIcon className="h-4 w-4" />
//           <span>{`${unassginedTasks.length} Tasks Unassigned`}</span>
//         </button>
//       )}
//       <Modal
//         closeModal={() => {
//           setIsOpen(false);
//         }}
//         isOpen={isOpen}
//       >
//         <div className="p-6">
//           <ModalHeading title="Unassigned Tasks" />
//           <div className="mt-6 space-y-4">
//             <div className="flex gap-6 text-sm font-semibold">
//               <div className="w-80">{"Task"}</div>
//               <div className="w-32">{"Persona"}</div>
//               <div className="w-32">{"Tasker"}</div>
//             </div>
//             {unassginedTasks.map((task) => (
//               <UnassignedTaskCard
//                 key={task._id?.toString() ?? Math.random()}
//                 task={task}
//               />
//             ))}
//           </div>
//         </div>
//       </Modal>
//     </>
//   );
// }

// function UnassignedTaskCard({ task }: { task: TaskData }) {
//   const updateTaskMutation = useUpdateTask();

//   return (
//     <div className="flex items-center gap-6 text-sm text-gray-600">
//       <div className="w-80 truncate">{task.title}</div>
//       <div className="w-32">
//         {task.tasker.userType === "persona" && (
//           <div
//             className={clsx(
//               "w-fit max-w-full truncate rounded-full px-2.5 py-1 text-xs",
//               task.taskerType === "external"
//                 ? "bg-slate-700 text-white"
//                 : "border-gray-500 bg-white text-gray-500"
//             )}
//           >
//             {task.tasker.persona}
//           </div>
//         )}
//       </div>
//       <div className="w-[200px]">
//         <Tasker
//           task={task}
//           onChange={({ task, taskerType, taskerEmail, taskerId }) => {
//             if (taskerId) {
//               // console.log({
//               //   _id: task._id,
//               //   taskerId: taskerId,
//               //   taskerType: taskerType,
//               // });
//               if (!task._id) return;
//               updateTaskMutation.mutate({
//                 update: {
//                   _id: task._id,
//                   taskerId: taskerId.toString(),
//                   taskerType: taskerType,
//                 },
//                 metadata: {
//                   phaseId: task?.phase?._id,
//                   journeyId: task?.journey?._id,
//                 },
//               });
//             } else if (taskerEmail) {
//               // console.log({
//               //   _id: task._id,
//               //   taskerEmail: taskerEmail,
//               //   taskerType: taskerType,
//               // });
//               if (!task._id) return;
//               updateTaskMutation.mutate({
//                 update: {
//                   _id: task._id,
//                   taskerEmail: taskerEmail,
//                   taskerType: taskerType,
//                 },
//                 metadata: {
//                   phaseId: task?.phase?._id,
//                   journeyId: task?.journey?._id,
//                 },
//               });
//             }
//           }}
//           // hasLabel={false}
//         />
//       </div>
//     </div>
//   );
// }

// const elements: { text: string; onClick: () => void }[] = [
//   { text: "Due Date", onClick: () => {} },
//   { text: "Estimates", onClick: () => {} },
//   { text: "Time Left", onClick: () => {} },
// ];

// function TeamFilter({
//   instanceName,
//   customerName,
// }: {
//   instanceName: string;
//   customerName: string;
// }) {
//   const journeyCtx = useJourney();
//   return (
//     <Pop
//       isActive={journeyCtx.state.view.responsibility !== "all"}
//       onCancelClick={() => {
//         journeyCtx.dispatch({
//           type: "VIEW_TEAM_UPDATE",
//           responsibility: "all",
//         });
//       }}
//       openTo={"right"}
//       button={
//         <span>
//           {journeyCtx.state.view.responsibility === "all"
//             ? "All"
//             : journeyCtx.state.view.responsibility === "my"
//             ? "Me"
//             : journeyCtx.state.view.responsibility === "internal"
//             ? instanceName
//             : customerName}
//         </span>
//       }
//       options={
//         <div className="space-y-3">
//           <p className="font-medium text-gray-700">
//             Whose tasks do you want to see?
//           </p>
//           <div className="flex gap-2">
//             <Pill
//               active={journeyCtx.state.view.responsibility === "all"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_TEAM_UPDATE",
//                   responsibility: "all",
//                 });
//               }}
//               text="All"
//             />
//             <Pill
//               active={journeyCtx.state.view.responsibility === "my"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_TEAM_UPDATE",
//                   responsibility: "my",
//                 });
//               }}
//               text={"Me"}
//             />
//             <Pill
//               active={journeyCtx.state.view.responsibility === "internal"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_TEAM_UPDATE",
//                   responsibility: "internal",
//                 });
//               }}
//               text={instanceName}
//             />
//             <Pill
//               active={journeyCtx.state.view.responsibility === "external"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_TEAM_UPDATE",
//                   responsibility: "external",
//                 });
//               }}
//               text={customerName}
//             />
//           </div>
//         </div>
//       }
//     />
//   );
// }

// function TaskStatusFilter() {
//   const journeyCtx = useJourney();
//   return (
//     <Pop
//       isActive={journeyCtx.state.view.status !== "All"}
//       onCancelClick={() => {
//         journeyCtx.dispatch({
//           type: "VIEW_STATUS_UPDATE",
//           status: "All",
//         });
//       }}
//       openTo={"right"}
//       button={<span>{journeyCtx.state.view.status}</span>}
//       options={
//         <div className="space-y-3">
//           <p className="font-medium  text-gray-700">
//             Which tasks do you want to see?
//           </p>
//           <div className="flex flex-wrap gap-2">
//             <Pill
//               active={journeyCtx.state.view.status === "All"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATUS_UPDATE",
//                   status: "All",
//                 });
//               }}
//               text="All"
//             />
//             <Pill
//               active={journeyCtx.state.view.status === "To Do"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATUS_UPDATE",
//                   status: "To Do",
//                 });
//               }}
//               text="To Do"
//             />
//             <Pill
//               active={journeyCtx.state.view.status === "In Progress"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATUS_UPDATE",
//                   status: "In Progress",
//                 });
//               }}
//               text="In Progress"
//             />
//             <Pill
//               active={journeyCtx.state.view.status === "Completed"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATUS_UPDATE",
//                   status: "Completed",
//                 });
//               }}
//               text="Completed"
//             />
//             <Pill
//               active={journeyCtx.state.view.status === "Canceled"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATUS_UPDATE",
//                   status: "Canceled",
//                 });
//               }}
//               text="Canceled"
//             />
//           </div>
//         </div>
//       }
//     />
//   );
// }

// function TaskStateFilter() {
//   const journeyCtx = useJourney();
//   return (
//     <Pop
//       isActive={journeyCtx.state.view.state !== "all"}
//       onCancelClick={() => {
//         journeyCtx.dispatch({
//           type: "VIEW_STATE_UPDATE",
//           state: "all",
//         });
//       }}
//       openTo={"right"}
//       button={
//         <span>
//           {journeyCtx.state.view.state === "active"
//             ? "Active"
//             : journeyCtx.state.view.state === "dependent"
//             ? "Dependent"
//             : "All"}
//         </span>
//       }
//       options={
//         <div className="space-y-3">
//           <p className="font-medium text-gray-700">
//             Which tasks do you want to see?
//           </p>
//           <div className="flex gap-2">
//             <Pill
//               active={journeyCtx.state.view.state === "all"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATE_UPDATE",
//                   state: "all",
//                 });
//               }}
//               text="All"
//             />
//             <Pill
//               active={journeyCtx.state.view.state === "active"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATE_UPDATE",
//                   state: "active",
//                 });
//               }}
//               text="Active"
//             />
//             <Pill
//               active={journeyCtx.state.view.state === "dependent"}
//               onClick={() => {
//                 journeyCtx.dispatch({
//                   type: "VIEW_STATE_UPDATE",
//                   state: "dependent",
//                 });
//               }}
//               text="Dependent"
//             />
//           </div>
//         </div>
//       }
//     />
//   );
// }

// function Pop({
//   button,
//   options,
//   openTo = "right",
//   isActive,
//   onCancelClick,
// }: {
//   button: JSX.Element;
//   options: JSX.Element;
//   openTo?: "left" | "right";
//   isActive: boolean;
//   onCancelClick: () => void;
// }) {
//   return (
//     <Popover className="relative text-sm">
//       {({ open }) => (
//         <>
//           <Popover.Button
//             className={clsx(
//               "flex items-center space-x-2 rounded-md border px-3 py-1 text-gray-600",
//               isActive && "border-primary-main text-primary-main"
//             )}
//           >
//             {button}
//             {isActive && (
//               <button
//                 onClick={(e) => {
//                   onCancelClick();
//                   e.stopPropagation();
//                 }}
//                 className="rounded-full p-1 hover:bg-primary-100"
//               >
//                 <XMarkIcon className="h-3 w-3 text-primary-main" />
//               </button>
//             )}
//             <ChevronUpIcon
//               className={clsx(
//                 open ? "rotate-180 text-gray-500" : "text-gray-400",
//                 "h-3 w-3 transition duration-300 ease-in-out"
//               )}
//               aria-hidden="true"
//             />
//           </Popover.Button>
//           <Transition.Child
//             as={Fragment}
//             enter="ease-out duration-300"
//             enterFrom="opacity-0"
//             enterTo="opacity-100"
//             leave="ease-in duration-200"
//             leaveFrom="opacity-100"
//             leaveTo="opacity-0"
//           >
//             <div className="fixed inset-0 z-10 bg-black bg-opacity-25" />
//           </Transition.Child>
//           <Transition
//             as={Fragment}
//             enter="transition ease-out duration-200"
//             enterFrom="opacity-0 translate-y-1"
//             enterTo="opacity-100 translate-y-0"
//             leave="transition ease-in duration-150"
//             leaveFrom="opacity-100 translate-y-0"
//             leaveTo="opacity-0 translate-y-1"
//           >
//             <Popover.Panel
//               className={clsx(
//                 "absolute z-10 mt-3 w-72",
//                 openTo === "left" ? "right-0" : "left-0"
//               )}
//             >
//               <div className="divide-y rounded-lg bg-white px-4 py-4 shadow-md">
//                 {options}
//               </div>
//             </Popover.Panel>
//           </Transition>
//         </>
//       )}
//     </Popover>
//   );
// }

function DisplayOptions() {
  // const displayOptions = getLocalStorageDisplaySettings();
  const journeyCtx = useJourney();
  return (
    <></>
    // <Popover className="relative text-sm">
    //   {({ open }) => (
    //     <>
    //       <Popover.Button
    //         className={
    //           "flex items-center space-x-2 rounded-md border px-3 py-1 text-gray-600"
    //         }
    //       >
    //         <span>Filters</span>
    //         <div className="w-4">
    //           {[
    //             journeyCtx.state.view.showSubtasks,
    //             journeyCtx.state.view.showCompleted,
    //             journeyCtx.state.view.showInactive,
    //           ].filter((e) => !e).length > 0 && (
    //             <span
    //               className={clsx(
    //                 "w-4 font-medium",
    //                 journeyCtx.state.isExternal
    //                   ? "text-embedPrimary"
    //                   : "text-primary-main"
    //               )}
    //             >
    //               {
    //                 [
    //                   journeyCtx.state.view.showSubtasks,
    //                   journeyCtx.state.view.showCompleted,
    //                   journeyCtx.state.view.showInactive,
    //                 ].filter((e) => !e).length
    //               }
    //             </span>
    //           )}
    //         </div>
    //         <ChevronDownIcon className={"h-3 w-3"} aria-hidden="true" />
    //       </Popover.Button>
    //       <Transition.Child
    //         as={Fragment}
    //         enter="ease-out duration-300"
    //         enterFrom="opacity-0"
    //         enterTo="opacity-100"
    //         leave="ease-in duration-200"
    //         leaveFrom="opacity-100"
    //         leaveTo="opacity-0"
    //       >
    //         <div className="fixed inset-0 z-10 bg-black bg-opacity-10" />
    //       </Transition.Child>
    //       <Transition
    //         as={Fragment}
    //         enter="transition ease-out duration-200"
    //         enterFrom="opacity-0 translate-y-1"
    //         enterTo="opacity-100 translate-y-0"
    //         leave="transition ease-in duration-150"
    //         leaveFrom="opacity-100 translate-y-0"
    //         leaveTo="opacity-0 translate-y-1"
    //       >
    //         <Popover.Panel className="absolute left-0 z-10 mt-3 w-72">
    //           <div className="divide-y rounded-lg bg-white px-4 py-4 shadow-md">
    //             <div className="space-y-3">
    //               <div className="text-md font-semibold">View</div>
    //               <div className="flex items-center justify-between">
    //                 <div className="text-gray-600">Hide Subtasks</div>
    //                 <VariantToggle
    //                   size="sm"
    //                   enabled={!journeyCtx.state.view.showSubtasks}
    //                   setEnabled={() => {
    //                     journeyCtx.dispatch({
    //                       type: "VIEW_SHOW_SUBTASKS_UPDATE",
    //                       showSubtasks: !journeyCtx.state.view.showSubtasks,
    //                     });
    //                   }}
    //                   color={journeyCtx.state.isExternal ? "embed" : "primary"}
    //                   focus={false}
    //                 />
    //               </div>
    //               <div className="flex items-center justify-between">
    //                 <div className="text-gray-600">Hide Completed Tasks</div>
    //                 <VariantToggle
    //                   size="sm"
    //                   enabled={!journeyCtx.state.view.showCompleted}
    //                   setEnabled={() => {
    //                     journeyCtx.dispatch({
    //                       type: "VIEW_COMPLETED_UPDATE",
    //                       showCompleted: !journeyCtx.state.view.showCompleted,
    //                     });
    //                   }}
    //                   focus={false}
    //                   color={journeyCtx.state.isExternal ? "embed" : "primary"}
    //                 />
    //               </div>
    //               <div className="flex items-center justify-between">
    //                 <div className="text-gray-600">Hide Inactive Tasks</div>
    //                 <VariantToggle
    //                   size="sm"
    //                   enabled={!journeyCtx.state.view.showInactive}
    //                   setEnabled={() => {
    //                     journeyCtx.dispatch({
    //                       type: "VIEW_SHOW_INACTIVE_UPDATE",
    //                       showInactive: !journeyCtx.state.view.showInactive,
    //                     });
    //                   }}
    //                   color={journeyCtx.state.isExternal ? "embed" : "primary"}
    //                   focus={false}
    //                 />
    //               </div>
    //               {/* <div className="flex items-center justify-between">
    //                 <div>View</div>
    //                 <div className="flex items-center space-x-2">
    //                   <Pill text="Indented" onClick={() => {}} active={true} />
    //                   <Pill
    //                     text="Collapsible"
    //                     onClick={() => {}}
    //                     active={false}
    //                   />
    //                   <div className="rounded border px-2 py-1 text-xs">
    //                     Collapsible
    //                   </div>
    //                 </div>
    //               </div> */}
    //             </div>
    //             {/* <div className="mt-3 pt-3">
    //               <div className="space-y-3">
    //                 <div className="text-md font-semibold">All Tasks</div>
    //                 <div className="flex gap-2">
    //                   {elements.map((element) => (
    //                     <Pill
    //                       text={element.text}
    //                       onClick={element.onClick}
    //                       active={false}
    //                     />
    //                   ))}
    //                 </div>
    //               </div>
    //             </div> */}
    //           </div>
    //         </Popover.Panel>
    //       </Transition>
    //     </>
    //   )}
    // </Popover>
  );
}

function Pill({
  text,
  onClick,
  disabled = false,
  active,
}: {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  active: boolean;
}) {
  return (
    <div
      className={clsx(
        "cursor-default rounded border px-2 py-1 text-xs text-gray-600",
        disabled
          ? "bg-gray-200"
          : active
          ? "border-primary-400 text-primary-main"
          : "hover:border-primary-400  cursor-pointer hover:text-primary-main"
      )}
      onClick={onClick}
    >
      {text}
    </div>
  );
}

const JourneyStage = ({ stage }: { stage: JourneyStageObj }) => {
  return (
    <div className="flex w-[360px] flex-1 flex-col overflow-auto">
      {/* <div className="px-4"> */}
      {!!stage._id && (
        <JourneyStageTasks stage={stage} stageId={stage._id.toString()} />
      )}
      {/* </div> */}
    </div>
  );
};

const JourneyStageTasks = ({
  stage,
  stageId,
}: {
  stage: JourneyStageObj;
  stageId: string;
}) => {
  // get journey tasks
  const { data: tasks } = useGetJourneyStageTasks(
    stage.journeyId.toString(),
    stageId
  );
  const journeyCtx = useJourney();
  const app = useRealmApp();
  const { slug } = useParams();
  // console.log(tasks);
  // console.log(app.currentUser)
  return (
    <>
      {tasks && (
        <div className="flex flex-1 flex-col space-y-5 overflow-auto">
          <StageMetaInfo tasks={tasks} stage={stage} />
          <div className="flex-1 space-y-4 overflow-auto pb-6">
            {tasks?.length === 0 && (
              <div className="flex justify-center px-6">
                <p className="text-sm text-gray-400">No tasks in this stage</p>
              </div>
            )}
            {tasks
              .filter((t) => {
                if (journeyCtx.state.view.responsibility === "all") {
                  return true;
                } else if (
                  journeyCtx.state.view.responsibility === "internal"
                ) {
                  return t.taskerType === "internal";
                } else if (
                  journeyCtx.state.view.responsibility === "external"
                ) {
                  return t.taskerType === "external";
                } else if (journeyCtx.state.view.responsibility === "my") {
                  return (
                    t.tasker.userType === "fullUser" &&
                    t.tasker._id.toString() === app.currentUser.id
                  );
                }
              })
              .filter((t) => {
                if (journeyCtx.state.view.showOnlyActiveAndIncompleteTasks) {
                  // console.log(t.title, !!t.dueDate, t.status !== "Completed");
                  return !!t.dueDate && t.status !== "Completed";
                } else {
                  return true;
                }
              })
              .filter((t) => {
                if (journeyCtx.state.view.showInactive) {
                  return true;
                } else {
                  return !!t.dueDate;
                }
              })
              .filter((t) => {
                if (journeyCtx.state.view.showCompleted) {
                  return true;
                } else {
                  return t.status !== "Completed";
                }
              })
              .filter((t) => {
                if (!journeyCtx.state.isExternal) return true;
                return t.isVisible;
              })
              .filter((t) => {
                if (journeyCtx.state.view.showSubtasks) {
                  return true;
                } else {
                  return !t.parentTask;
                }
              })
              .map((task) => (
                <TaskCardWithSubtasks
                  allTasks={tasks}
                  key={task._id ? task._id.toString() : Math.random()}
                  task={task}
                />
                // <JourneyTask
                //   key={task._id ? task._id.toString() : Math.random()}
                //   task={task}
                //   onCellClick={onCellClick}
                // />
              ))}
          </div>
        </div>
      )}
    </>
  );
};

const TaskCardWithSubtasks = ({
  task,
  allTasks,
}: {
  task: TaskData;
  allTasks: TaskData[];
}) => {
  const journeyCtx = useJourney();
  return (
    <div className="space-y-2">
      <TaskCard task={task} />
      {task.subTaskCount && task.subTaskCount > 0 && (
        <div className="space-y-2">
          {allTasks
            .filter(
              (t) => t.parentTask?._id.toString() === task._id?.toString()
            )
            .filter((t) => {
              if (!journeyCtx.state.isExternal) return true;
              return t.isVisible;
            })
            .filter((t) => {
              if (journeyCtx.state.view.showOnlyActiveAndIncompleteTasks) {
                return !!t.dueDate && t.status !== "Completed";
              } else {
                return true;
              }
            })
            .map((t) => (
              <div key={t._id?.toString() ?? Math.random()} className="pl-6">
                <TaskCard
                  task={t}
                  // isSubTask={true}
                />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const StageMetaInfo = ({
  tasks,
  stage,
}: {
  tasks: TaskData[];
  stage: JourneyStageObj;
}) => {
  const minDate = getMinDate({ tasks });
  const maxDate = getMaxDate({ tasks });

  const hasTasksActive = tasks.filter((t) => t.dueDate).length > 0;
  const completedTasks = tasks.filter((t) => t.completionDate);
  const progressPercentage =
    tasks.length > 0 ? (completedTasks.length / tasks.length) * 100 : 0;
  // console.log(progressPercentage);
  return (
    <div className="mx-6 space-y-2">
      <div className="flex items-center justify-between gap-4">
        <div className="truncate whitespace-nowrap border border-transparent text-lg font-medium capitalize tracking-wide">
          {stage.name}
        </div>
        <div className="flex items-center space-x-2">
          <span className="whitespace-nowrap text-xs text-gray-500">{`${completedTasks.length}/${tasks.length}`}</span>

          {/* <Button variant={"secondaryLink"} className="px-0">
            <PlusIcon className="h-4 w-4 text-gray-500" />
          </Button> */}
        </div>
      </div>
      {/* <div className="flex items-end justify-between">
        <div className="flex justify-between">
          {minDate && maxDate  (
            <p className="text-xs text-gray-500">
              {`${dayjs(minDate).format("MMM DD")} - ${dayjs(maxDate).format(
                "MMM DD"
              )}`}
            </p>
          ) : (
            <p className="text-xs text-gray-500">{`Waiting on Dependency`}</p>
          )}
        </div>
      </div> */}
      <div
        className={clsx(
          "w-full rounded-sm",
          hasTasksActive ? "bg-green-200/70" : "bg-gray-100"
        )}
      >
        <div
          className={clsx(
            "h-2 rounded-sm",
            progressPercentage === 100 ? "bg-green-500" : "bg-green-500"
          )}
          style={{
            width: `${progressPercentage}%`,
          }}
        />
      </div>
    </div>
  );
};

function getMinDate({ tasks }: { tasks: TaskData[] }) {
  let startingMinDate: undefined | Date = undefined;
  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    if (task.dueDate) {
      if (!startingMinDate) {
        startingMinDate = new Date(task.dueDate);
        break;
      }
    }
  }
  if (!startingMinDate) {
    return undefined;
  } else {
    return tasks.reduce(
      (min, p) =>
        p.dueDate
          ? new Date(p.dueDate) < min
            ? new Date(p.dueDate)
            : min
          : min,
      startingMinDate
    );
  }
}

function getMaxDate({ tasks }: { tasks: TaskData[] }) {
  let startingMaxDate: undefined | Date = undefined;
  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    if (task.dueDate) {
      if (!startingMaxDate) {
        startingMaxDate = new Date(task.dueDate);
        break;
      }
    }
  }
  if (!startingMaxDate) {
    return undefined;
  } else {
    return tasks.reduce(
      (max, p) =>
        p.dueDate
          ? new Date(p.dueDate) > max
            ? new Date(p.dueDate)
            : max
          : max,
      startingMaxDate
    );
  }
}

export function filterAndBuildTree(
  tasks: TaskData[],
  predicate: (t: TaskData) => boolean
): TaskWithChildren[] {
  const idMap = new Map<string, TaskData & { children: TaskData[] }>();
  const parentMap = new Map<string, string | undefined>();
  const childrenMap = new Map<string, string[]>();

  // build maps
  for (const t of tasks) {
    if (!t._id) continue;
    idMap.set(t._id, { ...t, children: [] });
    parentMap.set(t._id, t.parentTask?._id);
    if (t.parentTask?._id) {
      const arr = childrenMap.get(t.parentTask._id) ?? [];
      arr.push(t._id);
      childrenMap.set(t.parentTask._id, arr);
    }
  }

  const included = new Set<string>();

  // mark matching nodes, their ancestors, and—if a node matches—its whole subtree
  for (const t of tasks) {
    if (!predicate(t)) continue;

    // A) mark self + ancestors
    let cur: string | undefined = t._id;
    while (cur && !included.has(cur)) {
      included.add(cur);
      cur = parentMap.get(cur);
    }

    // B) mark full subtree under this node
    const queue = [t._id];
    while (queue.length) {
      const id = queue.shift()!;
      const kids = childrenMap.get(id) || [];
      for (const kid of kids) {
        if (!included.has(kid)) {
          included.add(kid);
          queue.push(kid);
        }
      }
    }
  }

  // now rebuild only the included nodes
  const roots: TaskWithChildren[] = [];
  for (const id of included) {
    const node = idMap.get(id)!;
    const pid = parentMap.get(id);
    if (pid && included.has(pid)) {
      idMap.get(pid)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

type TaskWithChildren = TaskData & {
  children: TaskData[];
};
